import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState, useRef } from "react";
import { EpubView } from "react-reader";
import JSZip from "jszip";
import CommentThread from "./CommentThread";
import ReaderChatModal from "./ReaderChatModal";
import EpubCFI from "epubjs/src/epubcfi";
import {
  cacheOfflineBook,
  getOfflineBookRecord,
  getOfflineBookMetadata,
  isOfflineBookAvailable,
  updateOfflineBookMetadata
} from "../utils/offlineLibrary";
import {
  getLogicalSpineGroup,
  getLogicalTocItemForHref,
  getNextLinearSpineTarget
} from "../utils/readerSpineNavigation";
import "./Button.css";
import "./UploadCard.css";
import "./ReaderPage.css";
import "./ReaderPage.dark.css";
const LIGHT_HIGHLIGHT_COLORS = ["#e29b90", "#d8b7c0", "#eed8a1", "#9db285", "#c8d9c9", "#7bc5aa"];
const DARK_HIGHLIGHT_COLORS = ["#ac2d3e72", "#9616c941", "#ffb70033", "#30ee002d", "#23907a50", "#ffffff39"];
const READER_DEFAULT_APPEARANCE = {
  theme: "default",
  textSize: 17,
  fontFamily: "EB Garamond",
  lineSpacing: 1.5
};
const READER_THEME_PRESETS = {
  default: { ...READER_DEFAULT_APPEARANCE, theme: "default" },
  paper: { ...READER_DEFAULT_APPEARANCE, theme: "paper" },
  dark: { ...READER_DEFAULT_APPEARANCE, theme: "dark" }
};
const READER_FONT_OPTIONS = [
  { id: "EB Garamond", label: "EB Garamond", previewClass: "reader-font-option--garamond" },
  { id: "Open Sans", label: "Open Sans", previewClass: "reader-font-option--open-sans" },
  { id: "Gelasio", label: "Gelasio", previewClass: "reader-font-option--gelasio" }
];
const READER_THEME_OPTIONS = [
  { id: "default", label: "Default", previewClass: "reader-appearance-theme-card--default" },
  { id: "paper", label: "Paper", previewClass: "reader-appearance-theme-card--paper" },
  { id: "dark", label: "Dark", previewClass: "reader-appearance-theme-card--dark" }
];
const READER_PROGRESS_BREAK = 1600;
const IS_TOUCH = navigator.maxTouchPoints > 0;
const READER_CHAPTER_END_SHOW_THRESHOLD = 18;
const READER_TOUCH_CHAPTER_END_RATIO = 0.985;
const APPENDED_CHAPTER_HREF_PREFIX = "fic-reader-appended-chapter";
const READER_PROGRESS_DEBUG = false;
const READER_COMMENT_DEBUG = false;
const flattenTocItems = (items = []) =>
  items.flatMap((item) => [
    { ...item, subitems: undefined },
    ...flattenTocItems(item?.subitems || [])
  ]);
const normalizeHrefPath = (href = "") => {
  const base = String(href).split("#")[0];
  try {
    return decodeURIComponent(base);
  } catch (e) {
    return base;
  }
};
const hrefsMatch = (left = "", right = "") => {
  const a = normalizeHrefPath(left);
  const b = normalizeHrefPath(right);
  return !!a && !!b && (a === b || a.endsWith(b) || b.endsWith(a));
};
const debugReaderProgress = (...args) => {
  if (!READER_PROGRESS_DEBUG) return;
  console.log("[reader-progress]", ...args);
};
const debugReaderComment = (...args) => {
  if (!READER_COMMENT_DEBUG) return;
  console.log("[reader-comment]", ...args);
};
const COMMENT_BLOCK_SELECTOR = "p, li, blockquote, div, section, article";
const summarizeCommentNode = (node) => {
  if (!node) return null;
  const text = (node.textContent || "").replace(/\s+/g, " ").trim();
  return {
    tag: node.tagName?.toLowerCase?.() || "",
    className:
      typeof node.className === "string"
        ? node.className
        : node.getAttribute?.("class") || "",
    text: text.slice(0, 180),
    childCount: node.childElementCount || 0
  };
};
const summarizeCommentRect = (rect) =>
  rect
    ? {
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
        left: Math.round(rect.left),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      }
    : null;
const summarizeCommentNeighborhood = (items = []) =>
  items.map((item) => ({
    index: item.index,
    isTarget: item.isTarget,
    tag: item.tag,
    className: item.className,
    text: item.text,
    rectTop: item.rect?.top ?? null,
    rectBottom: item.rect?.bottom ?? null,
    rectHeight: item.rect?.height ?? null
  }));
const getReaderAppearanceStorageKey = (userId) =>
  `reader-appearance-${userId || "guest"}`;
const getReaderChatPinnedStorageKey = (userId, epubId) =>
  `reader-chat-pinned-${userId || "guest"}-${epubId}`;
const mergeCommentCollections = (existingComments = [], incomingComments = []) => {
  const byId = new Map();

  [...existingComments, ...incomingComments].forEach((comment, index) => {
    if (!comment) return;
    const fallbackId = `fallback-${index}-${comment.cfi || ""}-${comment.created_at || ""}`;
    const key = String(comment.id ?? fallbackId);
    byId.set(key, {
      ...(byId.get(key) || {}),
      ...comment
    });
  });

  return Array.from(byId.values()).sort((left, right) => {
    const leftTime = new Date(left?.created_at || left?.updated_at || 0).getTime();
    const rightTime = new Date(right?.created_at || right?.updated_at || 0).getTime();
    return (rightTime || 0) - (leftTime || 0);
  });
};
const readOfflineBlobAsArrayBuffer = async (blob) => {
  if (!blob) return null;

  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }

  try {
    return await new Response(blob).arrayBuffer();
  } catch (error) {}

  return new Promise((resolve, reject) => {
    if (typeof FileReader === "undefined") {
      reject(new Error("Offline blob reader is unavailable"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(reader.error || new Error("Failed to read offline blob"));
    reader.readAsArrayBuffer(blob);
  });
};

// ── Debug log (remove before shipping) ────────────────────────────────────
const posixDirname = (path = "") => {
  const normalized = String(path || "").replace(/\\/g, "/");
  const lastSlash = normalized.lastIndexOf("/");
  return lastSlash === -1 ? "" : normalized.slice(0, lastSlash);
};

const joinEpubPath = (...parts) =>
  parts
    .filter(Boolean)
    .join("/")
    .replace(/\\/g, "/")
    .replace(/\/{2,}/g, "/");

const relativeEpubPath = (fromPath = "", toPath = "") => {
  const fromDir = posixDirname(fromPath);
  const fromParts = fromDir ? fromDir.split("/").filter(Boolean) : [];
  const toParts = String(toPath || "")
    .replace(/\\/g, "/")
    .split("/")
    .filter(Boolean);

  while (fromParts.length && toParts.length && fromParts[0] === toParts[0]) {
    fromParts.shift();
    toParts.shift();
  }

  return [...fromParts.map(() => ".."), ...toParts].join("/") || ".";
};

const stripUnsafeXmlChars = (value = "") =>
  Array.from(String(value)).filter((char) => {
    const code = char.charCodeAt(0);
    return (
      code === 0x09 ||
      code === 0x0a ||
      code === 0x0d ||
      (code >= 0x20 && code !== 0x7f)
    );
  }).join("");

const escapeXmlText = (value = "") =>
  stripUnsafeXmlChars(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const buildAppendedChapterLabel = (order) => `Added Chapter ${order}`;

const APPENDED_ITALIC_STYLE_RE = /font-style\s*:\s*italic/i;
const APPENDED_BOLD_STYLE_RE = /font-weight\s*:\s*(bold|[6-9]00)/i;
const APPENDED_ITALIC_CLASS_RE = /\b(italic|emphasis|em)\b/i;
const APPENDED_BOLD_CLASS_RE = /\b(bold|semibold|strong)\b/i;

const serializeAppendedInlineNode = (node) => {
  if (!node) return "";

  if (node.nodeType === Node.TEXT_NODE) {
    return escapeXmlText(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const tag = node.tagName.toLowerCase();
  const styleValue = String(node.getAttribute?.("style") || "").toLowerCase();
  const classValue = String(node.getAttribute?.("class") || "");
  const isItalicSpan =
    tag === "span" &&
    (APPENDED_ITALIC_STYLE_RE.test(styleValue) ||
      APPENDED_ITALIC_CLASS_RE.test(classValue));
  const isBoldSpan =
    tag === "span" &&
    (APPENDED_BOLD_STYLE_RE.test(styleValue) ||
      APPENDED_BOLD_CLASS_RE.test(classValue));
  const children = Array.from(node.childNodes)
    .map((child) => serializeAppendedInlineNode(child))
    .join("");

  if (tag === "br") {
    return "<br />";
  }
  if (tag === "em" || tag === "i" || tag === "cite" || tag === "var") {
    return `<em>${children}</em>`;
  }
  if (tag === "strong" || tag === "b") {
    return `<strong>${children}</strong>`;
  }
  if (isItalicSpan && isBoldSpan) {
    return `<strong><em>${children}</em></strong>`;
  }
  if (isItalicSpan) {
    return `<em>${children}</em>`;
  }
  if (isBoldSpan) {
    return `<strong>${children}</strong>`;
  }

  return children;
};

const appendSanitizedAppendedBlocks = (node, blocks) => {
  if (!node) return;

  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent || "").trim();
    if (!text) return;
    blocks.push(
      `<p>${escapeXmlText(text).replace(/\n/g, "<br />")}</p>`
    );
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const tag = node.tagName.toLowerCase();

  if (tag === "p" || tag === "div") {
    const content = Array.from(node.childNodes)
      .map((child) => serializeAppendedInlineNode(child))
      .join("");
    if (content.trim()) {
      blocks.push(`<p>${content}</p>`);
      return;
    }
  }

  if (tag === "blockquote") {
    const content = Array.from(node.childNodes)
      .map((child) => serializeAppendedInlineNode(child))
      .join("");
    if (content.trim()) {
      blocks.push(`<blockquote><p>${content}</p></blockquote>`);
      return;
    }
  }

  if (tag === "ul" || tag === "ol") {
    const items = Array.from(node.children)
      .filter((child) => child.tagName?.toLowerCase() === "li")
      .map((child) => `<li>${Array.from(child.childNodes).map((item) => serializeAppendedInlineNode(item)).join("")}</li>`)
      .join("");
    if (items.trim()) {
      blocks.push(`<${tag}>${items}</${tag}>`);
      return;
    }
  }

  if (tag === "h1" || tag === "h2" || tag === "h3") {
    const content = Array.from(node.childNodes)
      .map((child) => serializeAppendedInlineNode(child))
      .join("");
    if (content.trim()) {
      blocks.push(`<${tag}>${content}</${tag}>`);
      return;
    }
  }

  Array.from(node.childNodes).forEach((child) =>
    appendSanitizedAppendedBlocks(child, blocks)
  );
};

const convertPlainTextToAppendedHtml = (content = "") => {
  const normalized = String(content || "").replace(/\r\n?/g, "\n");
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (!paragraphs.length) {
    return `<p>${escapeXmlText(normalized).replace(/\n/g, "<br />")}</p>`;
  }

  return paragraphs
    .map((paragraph) => `<p>${escapeXmlText(paragraph).replace(/\n/g, "<br />")}</p>`)
    .join("\n");
};

const sanitizePastedChapterHtml = (rawHtml = "", fallbackText = "") => {
  if (!rawHtml.trim()) {
    return convertPlainTextToAppendedHtml(fallbackText);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, "text/html");
  const blocks = [];
  Array.from(doc.body.childNodes).forEach((node) =>
    appendSanitizedAppendedBlocks(node, blocks)
  );

  if (!blocks.length) {
    return convertPlainTextToAppendedHtml(fallbackText);
  }

  return blocks.join("\n");
};

const normalizeAppendedChapterText = (value = "") =>
  String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n?/g, "\n")
    .replace(/\n{3,}/g, "\n\n");

const readStoredUser = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("user") || "{}");
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return parsed;
  } catch (error) {
    return {};
  }
};

const normalizeStoredUserForApp = (user = {}, apiUrl = "") => {
  const nextUser = { ...user };
  const baseUrl = String(apiUrl || "").replace(/\/api\/?$/, "");
  const rawProfilePicture = nextUser.profilePicture;

  if (typeof rawProfilePicture === "string" && rawProfilePicture.trim()) {
    if (!/^https?:\/\//i.test(rawProfilePicture) && baseUrl) {
      nextUser.profilePicture = `${baseUrl}/${rawProfilePicture.replace(/^\/+/, "")}`;
    }
  } else {
    nextUser.profilePicture = null;
  }

  if ("isAdmin" in nextUser || "is_admin" in nextUser) {
    const isAdmin = Boolean(nextUser.is_admin ?? nextUser.isAdmin);
    nextUser.isAdmin = isAdmin;
    nextUser.is_admin = isAdmin;
  }

  return nextUser;
};

const buildAppendedChapterMarkup = ({ title, content, contentHtml }) => `<?xml version="1.0" encoding="utf-8"?>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>${escapeXmlText(title)}</title>
    <meta charset="utf-8" />
    <style>
      body {
        margin: 0;
        padding: 0 0 2rem;
      }
      h1 {
        margin: 0 0 1.25rem;
      }
      .fic-reader-appended-chapter__body {
        white-space: normal;
      }
      .fic-reader-appended-chapter__body p,
      .fic-reader-appended-chapter__body blockquote,
      .fic-reader-appended-chapter__body li {
        white-space: pre-wrap;
      }
      .fic-reader-appended-chapter__body p,
      .fic-reader-appended-chapter__body blockquote,
      .fic-reader-appended-chapter__body ul,
      .fic-reader-appended-chapter__body ol {
        margin: 0 0 1em;
      }
    </style>
  </head>
  <body>
    <h1>${escapeXmlText(title)}</h1>
    <div class="fic-reader-appended-chapter__body">${
      contentHtml || convertPlainTextToAppendedHtml(content)
    }</div>
  </body>
</html>`;

const buildAppendedChapterHref = (order) =>
  `${APPENDED_CHAPTER_HREF_PREFIX}-${order}.xhtml`;

const getAppendedChapterOrderFromHref = (href = "") => {
  const match = String(href || "")
    .split("#")[0]
    .match(/fic-reader-appended-chapter-(\d+)\.xhtml$/i);
  return match ? Number(match[1]) : null;
};

const getAppendedChapterForTocItem = (tocItems = [], tocIndex = -1, chapters = []) => {
  if (!Array.isArray(tocItems) || tocIndex < 0 || !Array.isArray(chapters) || !chapters.length) {
    return null;
  }

  const tocItem = tocItems[tocIndex];
  const hrefOrder = getAppendedChapterOrderFromHref(tocItem?.href || "");
  if (hrefOrder == null) {
    return null;
  }

  const exactMatch = chapters.find(
    (chapter) => Number(chapter.chapter_order) === hrefOrder
  );
  if (exactMatch) {
    return exactMatch;
  }

  const appendedTocIndex = tocItems
    .slice(0, tocIndex + 1)
    .filter((item) => getAppendedChapterOrderFromHref(item?.href || "") != null)
    .length - 1;

  return chapters[appendedTocIndex] || chapters[chapters.length - 1] || null;
};

const isZipBuffer = (buffer) => {
  if (!buffer) return false;
  const bytes = new Uint8Array(buffer);
  return bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07) &&
    (bytes[3] === 0x04 || bytes[3] === 0x06 || bytes[3] === 0x08);
};

const buildDerivedEpubWithAppendedChapters = async (
  sourceBuffer,
  chapters = []
) => {
  if (!sourceBuffer || !Array.isArray(chapters) || chapters.length === 0) {
    return sourceBuffer;
  }

  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const zip = await JSZip.loadAsync(sourceBuffer);
  const containerXml = await zip.file("META-INF/container.xml")?.async("string");
  if (!containerXml) {
    throw new Error("Missing EPUB container");
  }

  const containerDoc = parser.parseFromString(containerXml, "application/xml");
  const rootfile = containerDoc.querySelector("rootfile");
  const opfPath = rootfile?.getAttribute("full-path") || "";
  if (!opfPath) {
    throw new Error("Missing EPUB package path");
  }

  const opfXml = await zip.file(opfPath)?.async("string");
  if (!opfXml) {
    throw new Error("Missing EPUB package");
  }

  const opfDoc = parser.parseFromString(opfXml, "application/xml");
  const packageDir = posixDirname(opfPath);
  const manifest = opfDoc.querySelector("manifest");
  const spine = opfDoc.querySelector("spine");
  if (!manifest || !spine) {
    throw new Error("Missing EPUB manifest or spine");
  }

  chapters.forEach((chapter, index) => {
    const order = index + 1;
    const chapterId = `${APPENDED_CHAPTER_HREF_PREFIX}-${order}`;
    const chapterHref = buildAppendedChapterHref(order);
    const chapterPath = joinEpubPath(packageDir, chapterHref);

    if (!manifest.querySelector(`item[id="${chapterId}"]`)) {
      const manifestItem = opfDoc.createElement("item");
      manifestItem.setAttribute("id", chapterId);
      manifestItem.setAttribute("href", chapterHref);
      manifestItem.setAttribute("media-type", "application/xhtml+xml");
      manifest.appendChild(manifestItem);
    }

    if (!spine.querySelector(`itemref[idref="${chapterId}"]`)) {
      const itemRef = opfDoc.createElement("itemref");
      itemRef.setAttribute("idref", chapterId);
      spine.appendChild(itemRef);
    }

    zip.file(
      chapterPath,
      buildAppendedChapterMarkup({
        title: chapter.title || buildAppendedChapterLabel(order),
        content: chapter.content_text || chapter.content || "",
        contentHtml: chapter.content_html || chapter.contentHtml || ""
      })
    );
  });

  const navItem =
    opfDoc.querySelector('manifest item[properties~="nav"]') || null;
  if (navItem) {
    const navPath = joinEpubPath(packageDir, navItem.getAttribute("href") || "");
    const navXml = await zip.file(navPath)?.async("string");
    if (navXml) {
      const navDoc = parser.parseFromString(navXml, "application/xhtml+xml");
      const tocNav =
        navDoc.querySelector('nav[*|type="toc"]') ||
        navDoc.querySelector('nav[epub\\:type="toc"]') ||
        navDoc.querySelector("nav");

      if (tocNav) {
        let tocList = tocNav.querySelector("ol, ul");
        if (!tocList) {
          tocList = navDoc.createElement("ol");
          tocNav.appendChild(tocList);
        }

        chapters.forEach((chapter, index) => {
          const order = index + 1;
          const href = relativeEpubPath(
            navPath,
            joinEpubPath(packageDir, buildAppendedChapterHref(order))
          );
          if (tocList.querySelector(`a[href="${href}"]`)) return;

          const li = navDoc.createElement("li");
          const link = navDoc.createElement("a");
          link.setAttribute("href", href);
          link.textContent = chapter.title || buildAppendedChapterLabel(order);
          li.appendChild(link);
          tocList.appendChild(li);
        });

        zip.file(navPath, serializer.serializeToString(navDoc));
      }
    }
  }

  const ncxId = spine.getAttribute("toc");
  if (ncxId) {
    const ncxManifestItem = manifest.querySelector(`item[id="${ncxId}"]`);
    const ncxHref = ncxManifestItem?.getAttribute("href") || "";
    const ncxPath = joinEpubPath(packageDir, ncxHref);
    const ncxXml = ncxHref ? await zip.file(ncxPath)?.async("string") : "";
    if (ncxXml) {
      const ncxDoc = parser.parseFromString(ncxXml, "application/xml");
      const navMap = ncxDoc.querySelector("navMap");
      if (navMap) {
        let nextPlayOrder = Array.from(ncxDoc.querySelectorAll("navPoint")).reduce(
          (max, navPoint) =>
            Math.max(max, Number(navPoint.getAttribute("playOrder")) || 0),
          0
        );

        chapters.forEach((chapter, index) => {
          const order = index + 1;
          const href = relativeEpubPath(
            ncxPath,
            joinEpubPath(packageDir, buildAppendedChapterHref(order))
          );
          if (navMap.querySelector(`content[src="${href}"]`)) return;

          nextPlayOrder += 1;
          const navPoint = ncxDoc.createElement("navPoint");
          navPoint.setAttribute("id", `${APPENDED_CHAPTER_HREF_PREFIX}-nav-${order}`);
          navPoint.setAttribute("playOrder", String(nextPlayOrder));

          const navLabel = ncxDoc.createElement("navLabel");
          const textNode = ncxDoc.createElement("text");
          textNode.textContent = chapter.title || buildAppendedChapterLabel(order);
          navLabel.appendChild(textNode);

          const contentNode = ncxDoc.createElement("content");
          contentNode.setAttribute("src", href);

          navPoint.appendChild(navLabel);
          navPoint.appendChild(contentNode);
          navMap.appendChild(navPoint);
        });

        zip.file(ncxPath, serializer.serializeToString(ncxDoc));
      }
    }
  }

  zip.file(opfPath, serializer.serializeToString(opfDoc));
  return zip.generateAsync({ type: "arraybuffer" });
};

function ReaderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [showToc, setShowToc] = useState(false);
  const [isTocClosing, setIsTocClosing] = useState(false);
  const [epub, setEpub] = useState(null);
  const [epubData, setEpubData] = useState(null);
  const [readerBookVersion, setReaderBookVersion] = useState(0);
  const [location, setLocation] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [toc, setToc] = useState([]);
  const [atEnd, setAtEnd] = useState(false);
  const [currentTocIndex, setCurrentTocIndex] = useState(-1);
  const [currentSectionHref, setCurrentSectionHref] = useState("");
  const [selToolbar, setSelToolbar] = useState(null); // { step, x, y, cfi, text }
  const [manageSheet, setManageSheet] = useState(null); // { cfi, color, hasNote, noteText, highlightedText }
  const [noteSheet, setNoteSheet] = useState(null); // { cfi, color, text, isEditing }
  const [commentsSheet, setCommentsSheet] = useState(null); // { cfi, comments: [] }
  const [commentsData, setCommentsData] = useState({}); // { cfi: [...comments] }
  const [addCommentSheet, setAddCommentSheet] = useState(null); // { cfi, selectedText }
  const [isCommentsSheetClosing, setIsCommentsSheetClosing] = useState(false);
  const [isManageSheetClosing, setIsManageSheetClosing] = useState(false);
  const [isNoteSheetClosing, setIsNoteSheetClosing] = useState(false);
  const [isAddCommentSheetClosing, setIsAddCommentSheetClosing] = useState(false);
  const [isReaderToolClosing, setIsReaderToolClosing] = useState(false);
  const [activeReaderTool, setActiveReaderTool] = useState(null);
  const [readerLibraryTab, setReaderLibraryTab] = useState("highlights");
  const [swipedHighlightId, setSwipedHighlightId] = useState(null);
  const [readerAppearance, setReaderAppearance] = useState(READER_DEFAULT_APPEARANCE);
  const [isReaderAppearanceLoaded, setIsReaderAppearanceLoaded] = useState(false);
  const [isFontMenuOpen, setIsFontMenuOpen] = useState(false);
  const [readerLoadingMessage, setReaderLoadingMessage] = useState(
    "Loading the full file...."
  );
  const [readerHighlights, setReaderHighlights] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [appendedChapters, setAppendedChapters] = useState([]);
  const [areAppendedChaptersReady, setAreAppendedChaptersReady] = useState(false);
  const [isAddChapterOpen, setIsAddChapterOpen] = useState(false);
  const [chapterMenuOpenId, setChapterMenuOpenId] = useState(null);
  const [newChapterTitle, setNewChapterTitle] = useState("");
  const [newChapterContent, setNewChapterContent] = useState("");
  const [newChapterRichHtml, setNewChapterRichHtml] = useState("");
  const [isAddingChapter, setIsAddingChapter] = useState(false);
  const [addChapterError, setAddChapterError] = useState("");
  const [isChatPinned, setIsChatPinned] = useState(() => {
    try {
      const initialUserId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
      return (
        localStorage.getItem(getReaderChatPinnedStorageKey(initialUserId, id)) === "1"
      );
    } catch (e) {
      return false;
    }
  });
  const [isReaderChatOpen, setIsReaderChatOpen] = useState(false);
  const [pendingReaderChatQuote, setPendingReaderChatQuote] = useState(null);
  const [readerChatUnreadCount, setReaderChatUnreadCount] = useState(0);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [currentUser, setCurrentUser] = useState(() => readStoredUser());

  const renditionRef = useRef(null);
  const skipNext = useRef(false);
  const locationRef = useRef(null);
  const saveTimerRef = useRef(null);
  const lastProgressPostAtRef = useRef(0);
  const lastSavedProgressCfiRef = useRef("");
  const tocNavInProgressRef = useRef(false);
  const tocNavPendingRef = useRef(null);
  const tocRef = useRef([]);
  const currentTocIndexRef = useRef(-1);
  const currentSectionHrefRef = useRef("");
  const scrollNearEndRef = useRef(false);
  const highlightsRef = useRef([]);
  const commentAnchorsRef = useRef({});
  const commentsDataRef = useRef({});
  const commentsSheetRef = useRef(null);
  const contentsRef = useRef(null);
  const selPollRef = useRef(null);
  const lastSelectionPollKeyRef = useRef("");
  const commentsSheetCloseTimerRef = useRef(null);
  const tocCloseTimerRef = useRef(null);
  const manageSheetCloseTimerRef = useRef(null);
  const noteSheetCloseTimerRef = useRef(null);
  const addCommentSheetCloseTimerRef = useRef(null);
  const readerToolCloseTimerRef = useRef(null);
  const endPanelTimerRef = useRef(null);
  const readerToolOpenTimerRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const tempSearchHighlightRef = useRef(null);
  const tempSearchHighlightHrefRef = useRef(null);
  const tempSearchHighlightNodesRef = useRef([]);
  const tempSearchHighlightIsOverlayRef = useRef(false);
  const tempSearchHighlightTimerRef = useRef(null);
  const saveReaderAppearanceTimerRef = useRef(null);
  const readerAppearanceRef = useRef(READER_DEFAULT_APPEARANCE);
  const latestProgressRef = useRef(null);
  const progressLocationsRef = useRef([]);
  const progressLocationsReadyRef = useRef(false);
  const progressEpubCfiRef = useRef(new EpubCFI());
  const readerLoadRequestIdRef = useRef(0);
  const chapterEndPagesRef = useRef({});
  const logicalChapterMountsInProgressRef = useRef(new Set());
  const loadingMessageTimerRef = useRef(null);
  const notificationCommentTargetRef = useRef(null);
  const notificationProgressGuardRef = useRef(false);
  const notificationProgressPromptedRef = useRef(false);
  const notificationPromptTimerRef = useRef(null);
  const highlightSwipeStateRef = useRef({
    id: null,
    startX: 0,
    startY: 0,
    isSwiping: false
  });
  const pendingScrollRestoreRef = useRef(null);
  const restoringScrollPositionRef = useRef(false);
  const suppressHighlightOpenRef = useRef(false);
  const lastUsedColorRef = useRef(
    localStorage.getItem("highlight-last-color") || LIGHT_HIGHLIGHT_COLORS[0]
  );
  const addChapterEditorRef = useRef(null);
  const activeComments = commentsSheet
    ? commentsData[commentsSheet.cfi] || commentsSheet.comments || []
    : [];
  const activeCommentCount = _getCommentItemCount(activeComments);
  const currentUserId = Number(currentUser?.id) || 0;
  const currentUserIsAdmin = Boolean(
    currentUser?.is_admin ?? currentUser?.isAdmin
  );
  const currentEpubId = Number(id) || 0;
  const apiUrl = process.env.REACT_APP_API_URL;
  const getReaderChatSeenMessageStorageKey = useCallback(
    (conversationId) => `reader-chat-last-seen-${currentUserId}-${conversationId}`,
    [currentUserId]
  );
  const replaceReaderBookData = useCallback((nextBuffer) => {
    setEpubData(nextBuffer);
    setReaderBookVersion((prev) => prev + 1);
    setAtEnd(false);
    setTotalPages(null);
    setCurrentPage(null);
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.backgroundColor = "";
      document.body.classList.remove("reader-theme-body--dark");
      clearInterval(selPollRef.current);
      clearTimeout(commentsSheetCloseTimerRef.current);
      clearTimeout(tocCloseTimerRef.current);
      clearTimeout(manageSheetCloseTimerRef.current);
      clearTimeout(noteSheetCloseTimerRef.current);
      clearTimeout(addCommentSheetCloseTimerRef.current);
      clearTimeout(readerToolCloseTimerRef.current);
      clearTimeout(endPanelTimerRef.current);
      clearTimeout(readerToolOpenTimerRef.current);
      clearTimeout(tempSearchHighlightTimerRef.current);
      clearTimeout(saveReaderAppearanceTimerRef.current);
      clearTimeout(saveTimerRef.current);
      clearTimeout(loadingMessageTimerRef.current);
      clearTimeout(notificationPromptTimerRef.current);
      try {
        if (tempSearchHighlightRef.current && renditionRef.current) {
          clearTempSearchHighlight();
        }
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    const target = routerLocation.state?.notificationCommentTarget;
    if (!target?.cfi || !target?.commentId) return;

    notificationCommentTargetRef.current = {
      cfi: target.cfi,
      commentId: Number(target.commentId),
      replyId: target.replyId ? Number(target.replyId) : null
    };
    notificationProgressGuardRef.current = true;
    notificationProgressPromptedRef.current = false;

    navigate(`/epub/${id}/read`, { replace: true, state: null });
  }, [id, navigate, routerLocation.state]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const rememberToken = localStorage.getItem("rememberToken") || "";
    if (!rememberToken) return;

    fetch(`${apiUrl}/check-auth.php`, {
      method: "GET",
      credentials: "include",
      headers: rememberToken ? { "X-Remember-Token": rememberToken } : {},
      cache: "no-store"
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data?.valid || !data.user) return;
        const nextUser = normalizeStoredUserForApp({
          ...readStoredUser(),
          ...data.user,
        }, apiUrl);
        localStorage.setItem("user", JSON.stringify(nextUser));
        setCurrentUser(nextUser);
        window.dispatchEvent(
          new CustomEvent("profile-updated", { detail: nextUser })
        );
      })
      .catch(() => {});
  }, [apiUrl]);

  useEffect(() => {
    const surfaceColor =
      readerAppearance.theme === "dark"
        ? "#242424"
        : readerAppearance.theme === "paper"
          ? "#EDEDED"
          : "#ffffff";

    document.body.style.backgroundColor = surfaceColor;
    document.body.classList.toggle(
      "reader-theme-body--dark",
      readerAppearance.theme === "dark"
    );

    return () => {
      document.body.classList.remove("reader-theme-body--dark");
    };
  }, [readerAppearance.theme]);

  const loadReaderBook = useCallback(async () => {
    if (isOnline && !areAppendedChaptersReady) {
      return;
    }

    const requestId = readerLoadRequestIdRef.current + 1;
    readerLoadRequestIdRef.current = requestId;
    const isStaleRequest = () => readerLoadRequestIdRef.current !== requestId;

    const offlineRecord = await getOfflineBookRecord(currentUserId, currentEpubId);
    const progressSnapshot = getProgressSnapshot();
    const fallbackLocation =
      progressSnapshot?.cfi ||
      progressSnapshot?.sectionHref ||
      offlineRecord?.metadata?.reading_cfi ||
      offlineRecord?.metadata?.section_href ||
      null;
    let resolvedBookMetadata = offlineRecord?.metadata || null;

    if (!isOnline) {
        if (offlineRecord?.metadata) {
          const offlineBook = mergeBookWithOfflineState(offlineRecord.metadata);
          setEpub(offlineBook);
          pendingScrollRestoreRef.current =
            offlineBook.section_href
              ? {
                  href: offlineBook.section_href,
                  ratio:
                    offlineBook.section_scroll_ratio == null
                      ? null
                    : Number(offlineBook.section_scroll_ratio)
              }
            : null;
        debugReaderProgress("load-book-offline", {
          readingCfi: offlineBook.reading_cfi || "",
          sectionHref: offlineBook.section_href || "",
          sectionScrollRatio: offlineBook.section_scroll_ratio
        });
        if (fallbackLocation) {
          if (isStaleRequest()) return;
          setLocation(fallbackLocation);
          locationRef.current = fallbackLocation;
          skipNext.current = true;
        }
      }
      if (offlineRecord?.blob) {
        const offlineBuffer = await readOfflineBlobAsArrayBuffer(offlineRecord.blob);
        if (isStaleRequest()) return;
        replaceReaderBookData(offlineBuffer);
      }
      return;
    }

    try {
      const response = await apiFetch(`${apiUrl}/get-epub.php?id=${id}&uid=${currentUserId}`);
      const data = await response.json();
        if (data.success) {
          const nextBook = mergeBookWithOfflineState(data.epub, {
            preferLocalProgress: !data.epub?.reading_cfi
          });
          resolvedBookMetadata = nextBook;
          setEpub(nextBook);
          pendingScrollRestoreRef.current =
            nextBook.section_href
              ? {
                  href: nextBook.section_href,
                  ratio:
                    nextBook.section_scroll_ratio == null
                      ? null
                    : Number(nextBook.section_scroll_ratio)
                  }
                : null;
          debugReaderProgress("load-book-online", {
            serverReadingCfi: data.epub?.reading_cfi || "",
            mergedReadingCfi: nextBook.reading_cfi || "",
            sectionHref: nextBook.section_href || "",
            sectionScrollRatio: nextBook.section_scroll_ratio
          });
          const initialLocation = nextBook.reading_cfi || nextBook.section_href || null;
        if (initialLocation) {
          if (isStaleRequest()) return;
          setLocation(initialLocation);
          locationRef.current = initialLocation;
          skipNext.current = true;
        }
      }
    } catch (error) {
        if (offlineRecord?.metadata) {
          const offlineBook = mergeBookWithOfflineState(offlineRecord.metadata);
          setEpub(offlineBook);
          pendingScrollRestoreRef.current =
            offlineBook.section_href
              ? {
                  href: offlineBook.section_href,
                  ratio:
                    offlineBook.section_scroll_ratio == null
                      ? null
                    : Number(offlineBook.section_scroll_ratio)
              }
            : null;
        if (fallbackLocation) {
          if (isStaleRequest()) return;
          setLocation(fallbackLocation);
          locationRef.current = fallbackLocation;
          skipNext.current = true;
        }
      } else {
        console.error(error);
      }
    }

    try {
      const response = await apiFetch(
        `${apiUrl}/serve-epub.php?id=${id}&include_appended=0`
      );
      if (!response.ok) {
        throw new Error(`EPUB request failed with ${response.status}`);
      }
      const blob = await response.blob();
      const buffer = await readOfflineBlobAsArrayBuffer(blob);
      if (!isZipBuffer(buffer)) {
        throw new Error("Reader received an invalid EPUB payload");
      }

      let readerBuffer = buffer;
      if (appendedChapters.length) {
        try {
          readerBuffer = await buildDerivedEpubWithAppendedChapters(
            buffer,
            appendedChapters
          );
        } catch (mergeError) {
          readerBuffer = buffer;
        }
      }

      if (isStaleRequest()) return;
      replaceReaderBookData(readerBuffer);

      if (isOfflineBookAvailable(currentUserId, currentEpubId)) {
        await cacheOfflineBook({
          userId: currentUserId,
          epubId: currentEpubId,
          metadata: mergeBookWithOfflineState(resolvedBookMetadata || {}),
          buffer: readerBuffer
        });
      }
    } catch (error) {
      if (offlineRecord?.blob) {
        const offlineBuffer = await readOfflineBlobAsArrayBuffer(offlineRecord.blob);
        if (isStaleRequest()) return;
        replaceReaderBookData(offlineBuffer);
      } else {
        console.error(error);
      }
    }
  }, [
    areAppendedChaptersReady,
    appendedChapters.length,
    apiUrl,
    currentEpubId,
    currentUserId,
    id,
    isOnline,
    replaceReaderBookData
  ]);

  useEffect(() => {
    loadReaderBook();
  }, [loadReaderBook]);

  useEffect(() => {
    const appearanceStorageKey = getReaderAppearanceStorageKey(currentUserId);
    try {
      const cachedAppearance = localStorage.getItem(appearanceStorageKey);
      if (cachedAppearance) {
        setReaderAppearance(
          normalizeReaderAppearance(JSON.parse(cachedAppearance))
        );
        setIsReaderAppearanceLoaded(true);
      }
    } catch (e) {}

    let cancelled = false;

    apiFetch(`${apiUrl}/get-reader-preferences.php?uid=${currentUserId}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data?.success) return;
        const next = normalizeReaderAppearance(data.preferences);
        setReaderAppearance(next);
        setIsReaderAppearanceLoaded(true);
        try {
          localStorage.setItem(appearanceStorageKey, JSON.stringify(next));
        } catch (e) {}
      })
      .catch(() => {
        if (cancelled) return;
        if (!localStorage.getItem(appearanceStorageKey)) {
          setReaderAppearance(READER_DEFAULT_APPEARANCE);
          setIsReaderAppearanceLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiUrl, currentUserId]);

  const loadReaderHighlights = useCallback(async () => {
    commentAnchorsRef.current = _loadCommentAnchorStore();

    if (!isOnline) {
      const storedHighlights = getHighlightStore();
      syncReaderHighlights(storedHighlights);
      if (renditionRef.current) {
        storedHighlights.forEach((highlight) =>
          _applyToRendition(
            renditionRef.current,
            highlight.cfi,
            highlight.color,
            !!highlight.note_text
          )
        );
      }
      return;
    }

    try {
      const response = await apiFetch(
        `${apiUrl}/get-highlights.php?epub_id=${id}&uid=${currentUserId}`
      );
      const data = await response.json();
      if (data.success) {
        const loadedHighlights = [];
        data.highlights.forEach((h) => {
          if (_isCommentOnlyAnchor(h.cfi, h)) {
            _setCommentAnchorMeta(h.cfi, {
              highlighted_text:
                commentAnchorsRef.current[h.cfi]?.highlighted_text ||
                h.highlighted_text ||
                "",
              commentOnly: true,
              persisted: true
            });
            return;
          }
          loadedHighlights.push(h);
        });
        syncReaderHighlights(loadedHighlights);
        if (renditionRef.current) {
          loadedHighlights.forEach((h) =>
            _applyToRendition(
              renditionRef.current,
              h.cfi,
              h.color,
              !!h.note_text
            )
          );
        }
      }
    } catch (error) {
      console.error(error);
      const storedHighlights = getHighlightStore();
      syncReaderHighlights(storedHighlights);
    }
  }, [apiUrl, currentUserId, id, isOnline]);

  useEffect(() => {
    loadReaderHighlights();
  }, [loadReaderHighlights]);

  useEffect(() => {
    commentsDataRef.current = commentsData;
  }, [commentsData]);

  useEffect(() => {
    commentsSheetRef.current = commentsSheet;
  }, [commentsSheet]);

  const closeCommentsSheet = () => {
    if (!commentsSheet || isCommentsSheetClosing) return;
    setIsCommentsSheetClosing(true);
    clearTimeout(commentsSheetCloseTimerRef.current);
    commentsSheetCloseTimerRef.current = setTimeout(() => {
      setCommentsSheet(null);
      setIsCommentsSheetClosing(false);
    }, 160);
  };

  const closeToc = () => {
    if (!showToc || isTocClosing) return;
    setIsTocClosing(true);
    clearTimeout(tocCloseTimerRef.current);
    tocCloseTimerRef.current = setTimeout(() => {
      setShowToc(false);
      setIsTocClosing(false);
    }, 180);
  };

  const closeManageSheet = () => {
    if (!manageSheet || isManageSheetClosing) return;
    setIsManageSheetClosing(true);
    clearTimeout(manageSheetCloseTimerRef.current);
    manageSheetCloseTimerRef.current = setTimeout(() => {
      setManageSheet(null);
      setIsManageSheetClosing(false);
    }, 160);
  };

  const closeNoteSheet = () => {
    if (!noteSheet || isNoteSheetClosing) return;
    setIsNoteSheetClosing(true);
    clearTimeout(noteSheetCloseTimerRef.current);
    noteSheetCloseTimerRef.current = setTimeout(() => {
      setNoteSheet(null);
      setIsNoteSheetClosing(false);
    }, 160);
  };

  const closeAddCommentSheet = () => {
    if (!addCommentSheet || isAddCommentSheetClosing) return;
    setIsAddCommentSheetClosing(true);
    clearTimeout(addCommentSheetCloseTimerRef.current);
    addCommentSheetCloseTimerRef.current = setTimeout(() => {
      setAddCommentSheet(null);
      setIsAddCommentSheetClosing(false);
    }, 160);
  };

  const closeReaderTool = () => {
    if (!activeReaderTool || isReaderToolClosing) return;
    setIsReaderToolClosing(true);
    clearTimeout(readerToolCloseTimerRef.current);
    readerToolCloseTimerRef.current = setTimeout(() => {
      setActiveReaderTool(null);
      setIsReaderToolClosing(false);
    }, 160);
  };

  useEffect(() => {
    const dismiss = () => setSelToolbar(null);
    const evt = IS_TOUCH ? "touchstart" : "pointerdown";
    document.addEventListener(evt, dismiss);
    return () => document.removeEventListener(evt, dismiss);
  }, []);

  useEffect(() => {
    try {
      setIsChatPinned(
        localStorage.getItem(getReaderChatPinnedStorageKey(currentUserId, id)) === "1"
      );
    } catch (e) {
      setIsChatPinned(false);
    }
  }, [currentUserId, id]);

  useEffect(() => {
    try {
      localStorage.setItem(
        getReaderChatPinnedStorageKey(currentUserId, id),
        isChatPinned ? "1" : "0"
      );
    } catch (e) {}
  }, [currentUserId, id, isChatPinned]);

  useEffect(() => {
    tocRef.current = toc;
  }, [toc]);

  useEffect(() => {
    currentTocIndexRef.current = currentTocIndex;
  }, [currentTocIndex]);

  useEffect(() => {
    currentSectionHrefRef.current = currentSectionHref;
  }, [currentSectionHref]);

  useEffect(() => {
    if (!isAddChapterOpen) return;

    const focusTimer = window.setTimeout(() => {
      addChapterEditorRef.current?.focus();
    }, 40);

    return () => window.clearTimeout(focusTimer);
  }, [isAddChapterOpen]);

  useEffect(() => {
    if (activeReaderTool !== "search") return;
    const query = searchQuery.trim();
    const book = renditionRef.current?.book;
    const requestId = ++searchRequestIdRef.current;

    if (!query) {
      setSearchResults([]);
      setIsSearchLoading(false);
      return;
    }

    if (!book?.spine) {
      setSearchResults([]);
      setIsSearchLoading(false);
      return;
    }

    setIsSearchLoading(true);

    const timerId = setTimeout(() => {
      const sections = [];
      book.spine.each((section) => {
        if (section?.linear !== false) sections.push(section);
      });

      Promise.all(
        sections.map((section) =>
          section
            .load(book.load.bind(book))
            .then(() => {
              const matches =
                typeof section.search === "function"
                  ? section.search(query)
                  : section.find(query);
              const chapterLabel = _getChapterLabelForHref(section.href);

              return matches.map((match, index) => {
                const pageNumber = getPageNumberFromCfi(match.cfi);

                return {
                  id: `${section.href}-${index}-${match.cfi}`,
                  cfi: match.cfi,
                  chapterLabel,
                  excerpt: (match.excerpt || "").replace(/\s+/g, " ").trim(),
                  pageNumber
                };
              });
            })
            .catch(() => [])
        )
      )
        .then((results) => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults(results.flat());
          setIsSearchLoading(false);
        })
        .catch(() => {
          if (searchRequestIdRef.current !== requestId) return;
          setSearchResults([]);
          setIsSearchLoading(false);
        });
    }, 180);

    return () => {
      clearTimeout(timerId);
    };
  }, [activeReaderTool, searchQuery]);

  useEffect(() => {
    if (!isReaderAppearanceLoaded) return;
    if (!currentUserId) return;
    const appearanceStorageKey = getReaderAppearanceStorageKey(currentUserId);

    clearTimeout(saveReaderAppearanceTimerRef.current);
    saveReaderAppearanceTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(
          appearanceStorageKey,
          JSON.stringify(readerAppearance)
        );
      } catch (e) {}
      apiFetch(`${apiUrl}/save-reader-preferences.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          uid: currentUserId,
          theme: readerAppearance.theme,
          font_family: readerAppearance.fontFamily,
          font_size: readerAppearance.textSize,
          line_spacing: readerAppearance.lineSpacing
        })
      }).catch(() => {});
    }, 250);

    return () => {
      clearTimeout(saveReaderAppearanceTimerRef.current);
    };
  }, [apiUrl, currentUserId, isReaderAppearanceLoaded, readerAppearance]);

  useEffect(() => {
    if (!renditionRef.current || !isReaderAppearanceLoaded) return;
    readerAppearanceRef.current = readerAppearance;
    applyReaderAppearance(renditionRef.current, readerAppearance);
    applyLocations(renditionRef.current, readerAppearance);
    highlightsRef.current.forEach((highlight) => {
      _applyToRendition(
        renditionRef.current,
        highlight.cfi,
        normalizeHighlightColor(highlight.color, readerAppearance.theme),
        !!highlight.note_text
      );
    });
    Object.entries(commentsDataRef.current).forEach(([cfi, comments]) => {
      if (comments?.length) {
        _upsertCommentMarker(renditionRef.current, cfi, comments.length);
      }
    });
  }, [isReaderAppearanceLoaded, readerAppearance]);

  useEffect(() => {
    readerAppearanceRef.current = readerAppearance;
  }, [readerAppearance]);

  useEffect(() => {
    const readerIsReady = epub && epubData && totalPages !== null;
    clearTimeout(loadingMessageTimerRef.current);
    setReaderLoadingMessage("Loading the full book....");

    if (readerIsReady) return;

    loadingMessageTimerRef.current = setTimeout(() => {
      setReaderLoadingMessage("This is a big book bruh....");
    }, 2600);

    return () => {
      clearTimeout(loadingMessageTimerRef.current);
    };
  }, [epub, epubData, totalPages]);

  useEffect(() => {
    if (activeReaderTool !== "highlights") return;
    setReaderLibraryTab("highlights");
    setSwipedHighlightId(null);
    if (renditionRef.current) {
      _loadAndApplyCommentCounts(renditionRef.current);
    }
  }, [activeReaderTool]);

  useEffect(() => {
    if (readerLibraryTab !== "highlights") {
      setSwipedHighlightId(null);
    }
  }, [readerLibraryTab]);

  const isValidCfi = (cfi) =>
    typeof cfi === "string" && cfi.startsWith("epubcfi(");
  const getHighlightPalette = (theme = readerAppearanceRef.current.theme) =>
    theme === "dark" ? DARK_HIGHLIGHT_COLORS : LIGHT_HIGHLIGHT_COLORS;
  const getHighlightPaletteIndex = (color) => {
    if (!color) return -1;
    const normalized = String(color).toLowerCase();
    const lightIndex = LIGHT_HIGHLIGHT_COLORS.findIndex(
      (item) => item.toLowerCase() === normalized
    );
    if (lightIndex !== -1) return lightIndex;
    return DARK_HIGHLIGHT_COLORS.findIndex(
      (item) => item.toLowerCase() === normalized
    );
  };
  const normalizeHighlightColor = (
    color,
    theme = readerAppearanceRef.current.theme
  ) => {
    const palette = getHighlightPalette(theme);
    const colorIndex = getHighlightPaletteIndex(color);
    return palette[colorIndex === -1 ? 0 : colorIndex];
  };
  const getCommentMarkerColor = (theme = readerAppearanceRef.current.theme) =>
    theme === "dark" ? "#d5b2b3" : "#d07a7a";
  const getCommentMarkerStroke = (theme = readerAppearanceRef.current.theme) =>
    theme === "dark" ? "#242424" : "#ffffff";
  const getHighlightFillOpacity = (theme = readerAppearanceRef.current.theme) =>
    theme === "dark" ? "1" : "0.5";
  const apiFetch = (url, options = {}) =>
    {
      const rememberToken = localStorage.getItem("rememberToken") || "";
      const nextHeaders = {
        ...(options.headers || {})
      };
      if (rememberToken && !nextHeaders["X-Remember-Token"]) {
        nextHeaders["X-Remember-Token"] = rememberToken;
      }
      return fetch(url, {
        credentials: "include",
        cache: "no-store",
        ...options,
        headers: nextHeaders
      });
    };
  const loadAppendedChapters = useCallback(async () => {
    setAreAppendedChaptersReady(false);
    try {
      const response = await apiFetch(
        `${apiUrl}/get-appended-chapters.php?epub_id=${currentEpubId}`
      );
      const data = await response.json();
      const chapters =
        data?.success && Array.isArray(data.chapters) ? data.chapters : [];
      setAppendedChapters(chapters);
      setAreAppendedChaptersReady(true);
      return chapters;
    } catch (error) {
      setAppendedChapters([]);
      setAreAppendedChaptersReady(true);
      return [];
    }
  }, [apiUrl, currentEpubId]);
  const getBookScopedStorageKey = (prefix) =>
    `${prefix}-${currentUserId}-${currentEpubId}`;
  const readBookScopedJson = (prefix, fallback) => {
    try {
      return JSON.parse(
        localStorage.getItem(getBookScopedStorageKey(prefix)) || JSON.stringify(fallback)
      );
    } catch (e) {
      return fallback;
    }
  };
  const writeBookScopedJson = (prefix, value) => {
    try {
      localStorage.setItem(getBookScopedStorageKey(prefix), JSON.stringify(value));
    } catch (e) {}
  };
  useEffect(() => {
    loadAppendedChapters();
  }, [loadAppendedChapters]);

  useEffect(() => {
    setAreAppendedChaptersReady(false);
  }, [currentEpubId]);
  const syncAddChapterEditorState = useCallback(() => {
    const editor = addChapterEditorRef.current;
    if (!editor) {
      setNewChapterContent("");
      setNewChapterRichHtml("");
      return;
    }

    const plainText = normalizeAppendedChapterText(
      editor.innerText || editor.textContent || ""
    );
    const richHtml = sanitizePastedChapterHtml(editor.innerHTML || "", plainText);
    setNewChapterContent(plainText);
    setNewChapterRichHtml(richHtml);
  }, []);
  const closeAddChapterSheet = useCallback(() => {
    if (isAddingChapter) return;
    setIsAddChapterOpen(false);
    setAddChapterError("");
    setNewChapterTitle("");
    setNewChapterContent("");
    setNewChapterRichHtml("");
    if (addChapterEditorRef.current) {
      addChapterEditorRef.current.innerHTML = "";
    }
  }, [isAddingChapter]);
  const openAddChapterSheet = useCallback(() => {
    clearTimeout(tocCloseTimerRef.current);
    setShowToc(false);
    setIsTocClosing(false);
    setAddChapterError("");
    setNewChapterTitle("");
    setNewChapterContent("");
    setNewChapterRichHtml("");
    if (addChapterEditorRef.current) {
      addChapterEditorRef.current.innerHTML = "";
    }
    setIsAddChapterOpen(true);
  }, []);
  const handleAddChapterInput = useCallback(() => {
    syncAddChapterEditorState();
  }, [syncAddChapterEditorState]);
  const handleAddChapterPaste = useCallback((event) => {
    const pastedText = event.clipboardData?.getData("text/plain");
    const pastedHtml = event.clipboardData?.getData("text/html") || "";
    if (typeof pastedText !== "string") return;

    event.preventDefault();
    const insertionHtml = sanitizePastedChapterHtml(pastedHtml, pastedText);
    document.execCommand("insertHTML", false, insertionHtml);
    window.requestAnimationFrame(() => {
      syncAddChapterEditorState();
    });
  }, [syncAddChapterEditorState]);
  const handleAddChapterSubmit = useCallback(async () => {
    const trimmedTitle = newChapterTitle.trim();
    const trimmedContent = newChapterContent.replace(/\r\n?/g, "\n");
    if (!trimmedTitle) {
      setAddChapterError("Add a chapter title first.");
      return;
    }
    if (!trimmedContent.trim()) {
      setAddChapterError("Paste chapter content before adding it.");
      return;
    }

    setIsAddingChapter(true);
    setAddChapterError("");
    try {
      const sanitizedContentHtml = sanitizePastedChapterHtml(
        newChapterRichHtml,
        trimmedContent
      );
      const formData = new FormData();
      formData.append("epub_id", String(currentEpubId));
      formData.append("title", trimmedTitle);
      formData.append("content", trimmedContent);
      formData.append("content_html", sanitizedContentHtml);

      const response = await apiFetch(`${apiUrl}/add-appended-chapter.php`, {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Could not add that chapter right now.");
      }

      await loadAppendedChapters();
      await loadReaderBook();
      setIsAddChapterOpen(false);
      setNewChapterTitle("");
      setNewChapterContent("");
      setNewChapterRichHtml("");
      if (addChapterEditorRef.current) {
        addChapterEditorRef.current.innerHTML = "";
      }
    } catch (error) {
      setAddChapterError(
        error instanceof Error && error.message
          ? error.message
          : "Could not add that chapter right now."
      );
    } finally {
      setIsAddingChapter(false);
    }
  }, [
    apiUrl,
    currentEpubId,
    loadAppendedChapters,
    loadReaderBook,
    newChapterContent,
    newChapterRichHtml,
    newChapterTitle
  ]);
  const handleDeleteAppendedChapter = useCallback(async (chapterId) => {
    if (!currentUserIsAdmin || !chapterId) return;
    setChapterMenuOpenId(null);

    try {
      const formData = new FormData();
      formData.append("chapter_id", String(chapterId));

      const response = await apiFetch(`${apiUrl}/delete-appended-chapter.php`, {
        method: "POST",
        body: formData
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Could not delete that chapter right now.");
      }

      await loadAppendedChapters();
      await loadReaderBook();
    } catch (error) {
      window.alert(
        error instanceof Error && error.message
          ? error.message
          : "Could not delete that chapter right now."
      );
    }
  }, [apiUrl, currentUserIsAdmin, loadAppendedChapters, loadReaderBook]);
  const getOfflineQueue = () => {
    try {
      const raw = localStorage.getItem("reader-offline-queue-v1");
      const parsed = JSON.parse(raw || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };
  const saveOfflineQueue = (queue) => {
    try {
      localStorage.setItem("reader-offline-queue-v1", JSON.stringify(queue));
    } catch (e) {}
  };
  const enqueueOfflineMutation = (type, payload, dedupeKey = null) => {
    const queue = getOfflineQueue();
    const nextItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type,
      payload,
      dedupeKey,
      epubId: currentEpubId,
      userId: currentUserId,
      createdAt: Date.now()
    };
    const nextQueue = dedupeKey
      ? [...queue.filter((item) => item.dedupeKey !== dedupeKey), nextItem]
      : [...queue, nextItem];
    saveOfflineQueue(nextQueue);
  };
  const removeOfflineMutationIds = (ids) => {
    if (!ids.length) return;
    const removeSet = new Set(ids);
    saveOfflineQueue(getOfflineQueue().filter((item) => !removeSet.has(item.id)));
  };
  const getProgressSnapshot = () => readBookScopedJson("reader-progress-snapshot", null);
  const saveProgressSnapshot = (progress) => {
    if (!progress) return;
    writeBookScopedJson("reader-progress-snapshot", progress);
    if (isOfflineBookAvailable(currentUserId, currentEpubId)) {
      updateOfflineBookMetadata(currentUserId, currentEpubId, {
        progress_percent: progress.percent,
        reading_cfi: progress.cfi,
        chapter_title: progress.chapterTitle || "",
        section_href: progress.sectionHref || "",
        section_scroll_ratio:
          progress.sectionScrollRatio == null ? null : progress.sectionScrollRatio
      });
    }
  };
  const getHighlightStore = () => readBookScopedJson("reader-highlights-store", []);
  const saveHighlightStore = (highlights) => {
    writeBookScopedJson("reader-highlights-store", highlights);
  };
  const mergeBookWithOfflineState = (book = null, { preferLocalProgress = true } = {}) => {
    if (!book) return book;
    const offlineMetadata = getOfflineBookMetadata(currentUserId, currentEpubId);
    const progressSnapshot = getProgressSnapshot();

    const localProgress = progressSnapshot && preferLocalProgress
      ? {
          progress_percent: progressSnapshot.percent,
          reading_cfi: progressSnapshot.cfi,
          chapter_title: progressSnapshot.chapterTitle || "",
          section_href: progressSnapshot.sectionHref || "",
          section_scroll_ratio:
            progressSnapshot.sectionScrollRatio == null
              ? null
              : progressSnapshot.sectionScrollRatio
        }
      : {};

    return {
      ...book,
      ...(offlineMetadata || {}),
      ...localProgress
    };
  };
  const readerToolTitles = {
    search: "Search Book",
    appearance: "Reader Appearance",
    highlights: "Highlights"
  };
  const openReaderTool = (tool) => {
    closeToc();
    setIsReaderToolClosing(false);
    clearTimeout(readerToolCloseTimerRef.current);
    clearTimeout(readerToolOpenTimerRef.current);
    readerToolOpenTimerRef.current = setTimeout(() => {
      setActiveReaderTool(tool);
    }, 0);
  };
  const openReaderChat = () => {
    closeToc();
    closeReaderTool();
    setIsReaderChatOpen(true);
  };
  const openReaderChatWithQuote = (quoteText) => {
    const trimmedQuote = (quoteText || "").trim();
    if (!trimmedQuote) return;
    setPendingReaderChatQuote({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      quoteText: trimmedQuote,
      quoteType: "book"
    });
    openReaderChat();
  };

  useEffect(() => {
    if (!apiUrl || !currentUserId) {
      setReaderChatUnreadCount(0);
      return undefined;
    }

    let cancelled = false;

    const loadUnreadChatCount = async () => {
      try {
        const conversationsResponse = await fetch(
          `${apiUrl}/get-conversations.php?uid=${currentUserId}`,
          {
            credentials: "include",
            cache: "no-store"
          }
        );
        const conversationsData = await conversationsResponse.json();
        const conversations = Array.isArray(conversationsData?.conversations)
          ? conversationsData.conversations
          : [];

        if (!conversations.length) {
          if (!cancelled) setReaderChatUnreadCount(0);
          return;
        }

        const counts = await Promise.all(
          conversations.map(async (conversation) => {
            const messagesResponse = await fetch(
              `${apiUrl}/get-messages.php?uid=${currentUserId}&conversation_id=${conversation.id}`,
              {
                credentials: "include",
                cache: "no-store"
              }
            );
            const messagesData = await messagesResponse.json();
            const messages = Array.isArray(messagesData?.messages) ? messagesData.messages : [];
            let lastSeenMessageId = 0;
            try {
              lastSeenMessageId = Number(
                localStorage.getItem(getReaderChatSeenMessageStorageKey(conversation.id)) || 0
              );
            } catch (error) {}

            return messages.filter(
              (message) =>
                message?.message_type !== "system" &&
                Number(message?.id) > lastSeenMessageId &&
                Number(message?.user_id) !== Number(currentUserId)
            ).length;
          })
        );

        if (!cancelled) {
          setReaderChatUnreadCount(counts.reduce((sum, count) => sum + count, 0));
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load reader chat unread count:", error);
        }
      }
    };

    loadUnreadChatCount();

    const intervalId = window.setInterval(loadUnreadChatCount, 8000);
    const handleFocus = () => loadUnreadChatCount();
    window.addEventListener("focus", handleFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [apiUrl, currentUserId, getReaderChatSeenMessageStorageKey, isReaderChatOpen]);
  const syncReaderHighlights = (nextHighlights) => {
    highlightsRef.current = nextHighlights;
    setReaderHighlights(nextHighlights);
    saveHighlightStore(nextHighlights);
  };
  const saveReadingProgress = (progress, immediate = false) => {
    if (!progress || !currentUserId) return Promise.resolve();
    if (notificationProgressGuardRef.current) return Promise.resolve();

    saveProgressSnapshot(progress);
    debugReaderProgress("save-progress-request", {
      immediate,
      cfi: progress.cfi,
      percent: progress.percent,
      chapterTitle: progress.chapterTitle || "",
      sectionHref: progress.sectionHref || "",
      sectionScrollRatio: progress.sectionScrollRatio
    });

    if (!navigator.onLine) {
      enqueueOfflineMutation(
        "save-progress",
        {
          cfi: progress.cfi,
          percent: progress.percent,
          chapterTitle: progress.chapterTitle || ""
        },
        `save-progress-${currentUserId}-${currentEpubId}`
      );
      window.dispatchEvent(
        new CustomEvent("reader-progress-saved", {
          detail: {
            epubId: currentEpubId,
            progressPercent: progress.percent
          }
        })
      );
      return Promise.resolve();
    }

    const form = new FormData();
    form.append("uid", currentUserId);
    form.append("epub_id", id);
    form.append("cfi", progress.cfi);
    form.append("percent", progress.percent);
    form.append("chapter_title", progress.chapterTitle || "");
    form.append("section_href", progress.sectionHref || "");
    form.append(
      "section_scroll_ratio",
      progress.sectionScrollRatio == null ? "" : String(progress.sectionScrollRatio)
    );

    return apiFetch(`${apiUrl}/save-progress.php`, {
      method: "POST",
      body: form,
      keepalive: immediate
    })
      .then(() => {
        lastProgressPostAtRef.current = Date.now();
        lastSavedProgressCfiRef.current = progress.cfi || "";
        window.dispatchEvent(
          new CustomEvent("reader-progress-saved", {
            detail: {
              epubId: currentEpubId,
              progressPercent: progress.percent
            }
          })
        );
      })
      .catch(console.error);
  };
  const flushReadingProgress = () => {
    clearTimeout(saveTimerRef.current);
    return saveReadingProgress(latestProgressRef.current, true);
  };

  const getExistingProgressFloor = (cfi = "", sectionHref = "") => {
    const candidates = [
      latestProgressRef.current
        ? {
            percent: latestProgressRef.current.percent,
            cfi: latestProgressRef.current.cfi,
            sectionHref: latestProgressRef.current.sectionHref || ""
          }
        : null,
      epub
        ? {
            percent: epub.progress_percent,
            cfi: epub.reading_cfi,
            sectionHref: epub.section_href || ""
          }
        : null,
      (() => {
        const snapshot = getProgressSnapshot();
        return snapshot
          ? {
              percent: snapshot.percent,
              cfi: snapshot.cfi,
              sectionHref: snapshot.sectionHref || ""
            }
          : null;
      })()
    ].filter(Boolean);

    const normalizedHref = normalizeHrefPath(sectionHref);

    for (const candidate of candidates) {
      const candidatePercent = Number(candidate?.percent || 0);
      if (candidatePercent <= 0) continue;
      if (candidate?.cfi && candidate.cfi === cfi) {
        return candidatePercent;
      }
      if (
        normalizedHref &&
        candidate?.sectionHref &&
        hrefsMatch(candidate.sectionHref, normalizedHref)
      ) {
        return candidatePercent;
      }
    }

    return 0;
  };

  const queueProgressSave = (progress) => {
    if (!progress?.cfi) return;

    clearTimeout(saveTimerRef.current);

    const now = Date.now();
    const cfiChanged = progress.cfi !== lastSavedProgressCfiRef.current;
    const enoughTimeElapsed = now - lastProgressPostAtRef.current >= 1400;

    if (cfiChanged && enoughTimeElapsed) {
      saveReadingProgress(progress);
      return;
    }

    saveTimerRef.current = setTimeout(() => {
      saveReadingProgress(progress);
    }, 250);
  };

  const getCurrentSectionScrollState = () => {
    const rendition = renditionRef.current;
    const currentHref = normalizeHrefPath(
      rendition?.currentLocation?.()?.start?.href || currentSectionHrefRef.current || ""
    );
    const getContentsHref = (contents) => {
      if (!contents) return "";
      const sectionHref = rendition?.book?.spine?.get?.(contents.sectionIndex)?.href;
      return normalizeHrefPath(sectionHref || "");
    };
    const matchingContents =
      rendition
        ?.getContents?.()
        ?.find((contents) => hrefsMatch(getContentsHref(contents), currentHref)) || null;
    const contents = matchingContents || contentsRef.current;
    if (!contents?.document) return null;

    const sectionMetrics = getSectionContainerMetrics(contents);
    if (sectionMetrics) {
      const consumed = Math.max(
        0,
        Math.min(
          sectionMetrics.maxScrollTop,
          sectionMetrics.containerScrollTop - sectionMetrics.sectionTop
        )
      );
      const ratio =
        sectionMetrics.maxScrollTop <= 0
          ? 0
          : Math.max(0, Math.min(1, consumed / sectionMetrics.maxScrollTop));
      debugReaderProgress("capture-section-ratio", {
        mode: "container",
        currentHref,
        matchedHref: getContentsHref(contents),
        ratio,
        scrollTop: sectionMetrics.containerScrollTop,
        sectionTop: sectionMetrics.sectionTop,
        maxScrollTop: sectionMetrics.maxScrollTop,
        scrollHeight: sectionMetrics.sectionHeight,
        viewportHeight: sectionMetrics.viewportHeight
      });
      return {
        ratio,
        canScroll: sectionMetrics.maxScrollTop > 0
      };
    }

    const doc = contents.document;
    const docEl = doc.documentElement;
    const body = doc.body;
    const scrollingEl = doc.scrollingElement || docEl || body;
    const scrollTop = Math.max(
      contents.window?.scrollY || 0,
      scrollingEl?.scrollTop || 0,
      docEl?.scrollTop || 0,
      body?.scrollTop || 0
    );
    const viewportHeight = Math.max(
      contents.window?.innerHeight || 0,
      contents.window?.visualViewport?.height || 0,
      docEl?.clientHeight || 0,
      scrollingEl?.clientHeight || 0
    );
    const scrollHeight = Math.max(
      scrollingEl?.scrollHeight || 0,
      docEl?.scrollHeight || 0,
      body?.scrollHeight || 0
    );
    const maxScrollTop = Math.max(0, scrollHeight - viewportHeight);
    if (maxScrollTop <= 0) {
      return {
        ratio: 0,
        canScroll: false
      };
    }

    const ratio = Math.max(0, Math.min(1, scrollTop / maxScrollTop));
    debugReaderProgress("capture-section-ratio", {
      mode: "contents",
      currentHref,
      matchedHref: getContentsHref(contents),
      ratio,
      scrollTop,
      maxScrollTop,
      scrollHeight,
      viewportHeight
    });
    return {
      ratio,
      canScroll: true
    };
  };

  const getCurrentSectionScrollRatio = () => {
    const state = getCurrentSectionScrollState();
    return state?.ratio ?? null;
  };

  const getSectionContentsByHref = (href = "") => {
    const rendition = renditionRef.current;
    const targetHref = normalizeHrefPath(href);
    if (!rendition || !targetHref) return null;

    const contentsList = rendition.getContents?.() || [];
    return (
      contentsList.find((contents) => {
        const sectionHref = rendition.book?.spine?.get?.(contents.sectionIndex)?.href || "";
        return hrefsMatch(sectionHref, targetHref);
      }) || null
    );
  };

  const getRenditionScrollContainer = () => {
    const rendition = renditionRef.current;
    const managerContainer = rendition?.manager?.container;
    if (managerContainer) return managerContainer;
    return document.scrollingElement || document.documentElement || document.body || null;
  };

  const getContentsScrollMetrics = (contents) => {
    if (!contents?.document) return null;

    const doc = contents.document;
    const docEl = doc.documentElement;
    const body = doc.body;
    const scrollingEl = doc.scrollingElement || docEl || body;
    const scrollTop = Math.max(
      contents.window?.scrollY || 0,
      scrollingEl?.scrollTop || 0,
      docEl?.scrollTop || 0,
      body?.scrollTop || 0
    );
    const viewportHeight = Math.max(
      contents.window?.innerHeight || 0,
      contents.window?.visualViewport?.height || 0,
      docEl?.clientHeight || 0,
      scrollingEl?.clientHeight || 0
    );
    const scrollHeight = Math.max(
      scrollingEl?.scrollHeight || 0,
      docEl?.scrollHeight || 0,
      body?.scrollHeight || 0
    );
    const maxScrollTop = Math.max(0, scrollHeight - viewportHeight);

    return {
      scrollingEl,
      scrollTop,
      viewportHeight,
      scrollHeight,
      maxScrollTop
    };
  };

  const getSectionContainerMetrics = (contents) => {
    const frameElement = contents?.window?.frameElement;
    const scrollContainer = getRenditionScrollContainer();
    if (!frameElement || !scrollContainer?.getBoundingClientRect) return null;

    const containerRect = scrollContainer.getBoundingClientRect();
    const frameRect = frameElement.getBoundingClientRect();
    const scrollTop = Number(scrollContainer.scrollTop || 0);
    const sectionTop = frameRect.top - containerRect.top + scrollTop;
    const sectionHeight = Math.max(
      frameRect.height || 0,
      Number(frameElement.offsetHeight || 0),
      Number(frameElement.clientHeight || 0)
    );
    const viewportHeight = Math.max(
      Number(scrollContainer.clientHeight || 0),
      containerRect.height || 0
    );
    const maxScrollTop = Math.max(0, sectionHeight - viewportHeight);

    return {
      frameElement,
      scrollContainer,
      sectionTop,
      sectionHeight,
      viewportHeight,
      containerScrollTop: scrollTop,
      maxScrollTop
    };
  };

  const restoreSectionScrollPosition = (pendingRestore, attempt = 0) => {
    const targetContents =
      getSectionContentsByHref(pendingRestore?.href) || contentsRef.current || null;
    const sectionMetrics = getSectionContainerMetrics(targetContents);
    const metrics = sectionMetrics || getContentsScrollMetrics(targetContents);
    const targetHref = normalizeHrefPath(pendingRestore?.href || "");
    const matchedHref = normalizeHrefPath(
      renditionRef.current?.book?.spine?.get?.(targetContents?.sectionIndex)?.href || ""
    );

    debugReaderProgress("restore-attempt", {
      attempt,
      targetHref,
      matchedHref,
      pendingRatio: pendingRestore?.ratio,
      hasTargetContents: !!targetContents,
      restoreMode: sectionMetrics ? "container" : "contents",
      maxScrollTop: metrics?.maxScrollTop ?? null,
      scrollHeight:
        sectionMetrics?.sectionHeight ?? metrics?.scrollHeight ?? null,
      viewportHeight: metrics?.viewportHeight ?? null
    });

    if (!targetContents?.window || !metrics) {
      if (attempt >= 8) {
        restoringScrollPositionRef.current = false;
        debugReaderProgress("restore-abort-no-contents", {
          targetHref,
          attempt
        });
        syncLocationFromRendition();
        return;
      }
      window.setTimeout(
        () => restoreSectionScrollPosition(pendingRestore, attempt + 1),
        80
      );
      return;
    }

    const targetTop =
      metrics.maxScrollTop <= 0
        ? 0
        : Math.round(metrics.maxScrollTop * pendingRestore.ratio);
    if (sectionMetrics?.scrollContainer) {
      sectionMetrics.scrollContainer.scrollTop = Math.max(
        0,
        Math.round(sectionMetrics.sectionTop + targetTop)
      );
    } else {
      targetContents.window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "auto"
      });
    }

    window.setTimeout(() => {
      const nextSectionMetrics = getSectionContainerMetrics(targetContents);
      const nextMetrics = nextSectionMetrics || getContentsScrollMetrics(targetContents);
      const actualRatio =
        nextSectionMetrics?.maxScrollTop > 0
          ? Math.max(
              0,
              Math.min(
                1,
                (nextSectionMetrics.containerScrollTop - nextSectionMetrics.sectionTop) /
                  nextSectionMetrics.maxScrollTop
              )
            )
          : nextMetrics?.maxScrollTop > 0
            ? nextMetrics.scrollTop / nextMetrics.maxScrollTop
          : 0;
      const ratioDelta = Math.abs(actualRatio - pendingRestore.ratio);
      const heightChanged =
        !!nextMetrics &&
        Math.abs(
          (nextSectionMetrics?.sectionHeight || nextMetrics.scrollHeight || 0) -
            (sectionMetrics?.sectionHeight || metrics.scrollHeight || 0)
        ) > 12;
      const shouldRetry = attempt < 8 && (ratioDelta > 0.025 || heightChanged);

      debugReaderProgress("restore-result", {
        attempt,
        targetHref,
        matchedHref,
        pendingRatio: pendingRestore?.ratio,
        restoreMode: nextSectionMetrics ? "container" : "contents",
        actualRatio,
        ratioDelta,
        heightChanged,
        nextScrollTop:
          nextSectionMetrics?.containerScrollTop ?? nextMetrics?.scrollTop ?? null,
        nextMaxScrollTop: nextMetrics?.maxScrollTop ?? null
      });

      if (shouldRetry) {
        window.requestAnimationFrame(() => {
          restoreSectionScrollPosition(pendingRestore, attempt + 1);
        });
        return;
      }

      restoringScrollPositionRef.current = false;
      debugReaderProgress("restore-complete", {
        targetHref,
        matchedHref,
        finalRatio: actualRatio
      });
      syncLocationFromRendition();
    }, 120);
  };

  const flushOfflineQueue = useCallback(async () => {
    if (!navigator.onLine || !apiUrl || !currentUserId) return;

    const queue = getOfflineQueue()
      .filter(
        (item) =>
          Number(item.userId) === Number(currentUserId)
      )
      .sort((left, right) => Number(left.createdAt || 0) - Number(right.createdAt || 0));

    if (!queue.length) return;

    const completedIds = [];
    let touchedCurrentBook = false;

    for (const item of queue) {
      try {
        if (item.type === "save-progress") {
          const form = new FormData();
          form.append("uid", currentUserId);
          form.append("epub_id", item.epubId);
          form.append("cfi", item.payload.cfi);
          form.append("percent", item.payload.percent);
          form.append("chapter_title", item.payload.chapterTitle || "");
          form.append("section_href", item.payload.sectionHref || "");
          form.append(
            "section_scroll_ratio",
            item.payload.sectionScrollRatio == null ? "" : String(item.payload.sectionScrollRatio)
          );
          await apiFetch(`${apiUrl}/save-progress.php`, {
            method: "POST",
            body: form,
            keepalive: true
          });
        }

        if (item.type === "upsert-highlight") {
          const form = new FormData();
          form.append("uid", currentUserId);
          form.append("epub_id", item.epubId);
          form.append("cfi", item.payload.cfi);
          form.append("color", item.payload.color || "");
          form.append("note_text", item.payload.noteText || "");
          form.append("highlighted_text", item.payload.highlightedText || "");
          if (item.payload.commentOnly) {
            form.append("comment_only", "1");
          }
          await apiFetch(`${apiUrl}/add-highlight.php`, {
            method: "POST",
            body: form
          });
        }

        if (item.type === "delete-highlight") {
          const form = new FormData();
          form.append("uid", currentUserId);
          form.append("epub_id", item.epubId);
          form.append("cfi", item.payload.cfi);
          await apiFetch(`${apiUrl}/delete-highlight.php`, {
            method: "POST",
            body: form
          });
        }

        if (item.type === "create-comment") {
          const form = new FormData();
          form.append("uid", currentUserId);
          form.append("epub_id", item.epubId);
          form.append("cfi", item.payload.cfi);
          form.append("text", item.payload.text);
          form.append("selected_excerpt", item.payload.selectedText || "");
          form.append("chapter_title", item.payload.chapterTitle || "");
          await apiFetch(`${apiUrl}/create-comment.php`, {
            method: "POST",
            body: form
          });
        }

        completedIds.push(item.id);
        if (Number(item.epubId) === currentEpubId) {
          touchedCurrentBook = true;
        }
      } catch (error) {
        console.error("Failed to flush offline queue item:", error);
        break;
      }
    }

    removeOfflineMutationIds(completedIds);

    if (touchedCurrentBook) {
      loadReaderHighlights();
      _refreshCommentsSync();
    }
  }, [apiUrl, currentEpubId, currentUserId, loadReaderHighlights]);
  const escapeRegExp = (value = "") =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const _getChapterLabelForHref = (href = "") => {
    const navItems = flattenTocItems(
      renditionRef.current?.book?.navigation?.toc || []
    );
    const hrefPath = normalizeHrefPath(href);
    const match = navItems.find((item) => {
      return hrefsMatch(item.href || "", hrefPath);
    });
    return match?.label?.trim() || "Chapter";
  };
  const getNextChapterFromBook = () => {
    const book = renditionRef.current?.book;
    const flatToc = tocRef.current;
    const activeSectionHref = currentSectionHrefRef.current;
    const nextTarget = getNextLinearSpineTarget(book, activeSectionHref, flatToc);
    return nextTarget?.isChapterBoundary ? nextTarget.tocItem : null;
  };
  const renderSearchExcerpt = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, "ig"));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <strong key={`${part}-${index}`}>{part}</strong>
      ) : (
        part
      )
    );
  };
  const withAlpha = (color, alpha = 0.5) => {
    if (!color) return `rgba(226, 155, 144, ${alpha})`;
    const hex = color.replace("#", "");
    if (hex.length !== 6) return color;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  const normalizeReaderAppearance = (appearance = {}) => {
    const theme = READER_THEME_PRESETS[appearance.theme]
      ? appearance.theme
      : READER_DEFAULT_APPEARANCE.theme;
    const fontFamily = READER_FONT_OPTIONS.some(
      (option) => option.id === appearance.fontFamily || option.id === appearance.font_family
    )
      ? appearance.fontFamily || appearance.font_family
      : READER_THEME_PRESETS[theme].fontFamily;
    const textSize = Math.min(
      24,
      Math.max(
        12,
        Number(
          appearance.textSize ??
            appearance.fontSize ??
            appearance.font_size ??
            READER_THEME_PRESETS[theme].textSize
        ) || READER_THEME_PRESETS[theme].textSize
      )
    );
    const lineSpacing = Math.min(
      2,
      Math.max(
        1,
        Number(
          appearance.lineSpacing ??
            appearance.line_spacing ??
            READER_THEME_PRESETS[theme].lineSpacing
        ) || READER_THEME_PRESETS[theme].lineSpacing
      )
    );

    return {
      theme,
      fontFamily,
      textSize,
      lineSpacing: Number(lineSpacing.toFixed(2))
    };
  };
  const getReaderFontStack = (fontFamily) => {
    switch (fontFamily) {
      case "Open Sans":
        return "\"Open Sans\", sans-serif";
      case "Gelasio":
        return "\"Gelasio\", serif";
      default:
        return "\"EB Garamond\", serif";
    }
  };
  const applyReaderAppearanceToContents = (contents, appearance) => {
    if (!contents?.document) return;

    const doc = contents.document;
    let fontLink = doc.getElementById("reader-fonts-link");
    if (!fontLink) {
      fontLink = doc.createElement("link");
      fontLink.id = "reader-fonts-link";
      fontLink.rel = "stylesheet";
      fontLink.href =
        "https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,600;1,400&family=Gelasio:ital,wght@0,400;0,500;0,600;1,400&family=Open+Sans:wght@400&display=swap";
      doc.head.appendChild(fontLink);
    }

    let style = doc.getElementById("reader-appearance-style");
    if (!style) {
      style = doc.createElement("style");
      style.id = "reader-appearance-style";
      doc.head.appendChild(style);
    }

    const fontStack = getReaderFontStack(appearance.fontFamily);
    const isDarkTheme = appearance.theme === "dark";
    const pageBackground = isDarkTheme
      ? "#242424"
      : appearance.theme === "paper"
        ? "#EDEDED"
        : "#ffffff";
    const pageText = isDarkTheme ? "rgba(255, 255, 255, 0.92)" : "#241213";
    const pageLink = isDarkTheme ? "#f0d1d1" : "#5d2021";
    const selectionColor = isDarkTheme
      ? "rgba(213, 178, 179, 0.28)"
      : "rgba(208, 122, 122, 0.28)";
    style.textContent = `
      html, body {
        background: ${pageBackground} !important;
        color: ${pageText} !important;
      }
      body, p, div, span, li, blockquote, td, th, a {
        font-family: ${fontStack} !important;
        color: ${pageText} !important;
      }
      body {
        line-height: ${appearance.lineSpacing} !important;
      }
      p {
        margin-bottom: 1.1em !important;
        font-size: ${appearance.textSize}px !important;
        text-align: justify !important;
      }
      h1, h2, h3 {
        margin-top: 7rem !important;
        margin-bottom: 1em !important;
        color: ${pageText} !important;
      }
      a { color: ${pageLink} !important; }
      ::selection { background: ${selectionColor} !important; }
      svg { pointer-events: none !important; }
      svg [data-highlight-cfi] { pointer-events: auto !important; cursor: pointer !important; }
      svg g[data-highlight-cfi] rect,
      svg g[data-highlight-cfi] line,
      svg g[data-highlight-cfi] path {
        mix-blend-mode: ${isDarkTheme ? "screen" : "multiply"} !important;
        opacity: 1 !important;
      }
      svg rect, svg line, svg circle, svg path { pointer-events: auto !important; }
      svg .epub-note-dot {
        pointer-events: none !important;
      }
      .epub-comment-anchor {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-end !important;
        width: 30px !important;
        height: 20px !important;
        box-sizing: border-box !important;
        margin-left: 0 !important;
        text-decoration: none !important;
        pointer-events: auto !important;
        cursor: pointer !important;
        z-index: 20 !important;
      }
    `;
  };
  const applyReaderAppearance = (rendition, appearance) => {
    if (!rendition) return;
    rendition.themes.fontSize(`${appearance.textSize}px`);
    rendition.getContents().forEach((contents) =>
      applyReaderAppearanceToContents(contents, appearance)
    );
  };
  const formatReaderToolDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(
      String(dateStr).includes("T")
        ? dateStr
        : String(dateStr).replace(" ", "T") + "Z"
    );
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };
  const getPageNumberFromCfi = (cfi) => {
    if (!cfi) return null;

    const progressLocations = progressLocationsRef.current;
    if (progressLocations.length) {
      try {
        let low = 0;
        let high = progressLocations.length;

        while (low < high) {
          const mid = Math.floor((low + high) / 2);
          const comparison = progressEpubCfiRef.current.compare(cfi, progressLocations[mid]);
          if (comparison > 0) {
            low = mid + 1;
          } else {
            high = mid;
          }
        }

        const stableIndex = low >= progressLocations.length ? progressLocations.length - 1 : low;
        if (stableIndex >= 0) return stableIndex + 1;
      } catch (e) {}
    }

    const book = renditionRef.current?.book;
    if (!book?.locations?.total) return null;
    try {
      const idx = book.locations.locationFromCfi(cfi);
      return idx != null ? idx + 1 : null;
    } catch (e) {
      return null;
    }
  };
  const readerHighlightEntries = [...readerHighlights]
    .filter((item) => item.highlighted_text)
    .sort((a, b) => {
      const aDate = new Date(a.created_at || a.updated_at || 0).getTime();
      const bDate = new Date(b.created_at || b.updated_at || 0).getTime();
      return (bDate || 0) - (aDate || 0);
    })
    .map((item, index) => ({
      id: `highlight-${item.cfi}-${index}`,
      cfi: item.cfi,
      excerpt: item.highlighted_text || "",
      text: item.note_text || "",
      color: normalizeHighlightColor(item.color),
      createdAt: item.created_at || item.updated_at || "",
      pageNumber: getPageNumberFromCfi(item.cfi)
    }));
  const readerCommentEntries = Object.entries(commentsData)
    .flatMap(([cfi, comments]) =>
      (comments || [])
        .filter((comment) => Number(comment.user_id) === Number(currentUserId))
        .map((comment, index) => ({
          id: `comment-${comment.id || `${cfi}-${index}`}`,
          commentId: comment.id,
          cfi,
          text: comment.text || "",
          excerpt: comment.selected_excerpt || "",
          createdAt: comment.created_at || "",
          pageNumber: getPageNumberFromCfi(cfi)
        }))
    )
    .sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime();
      const bDate = new Date(b.createdAt || 0).getTime();
      return (bDate || 0) - (aDate || 0);
    });
  const loadReaderCommentEntries = useCallback(() => {
    if (!navigator.onLine || !apiUrl || !currentUserId || !currentEpubId) {
      return;
    }

    apiFetch(
      `${apiUrl}/get-user-comments.php?epub_id=${currentEpubId}&uid=${currentUserId}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (!data?.success || !Array.isArray(data.comments)) return;

        const groupedByCfi = data.comments.reduce((accumulator, comment) => {
          const cfi = comment?.cfi || "";
          if (!cfi) return accumulator;
          if (!accumulator[cfi]) {
            accumulator[cfi] = [];
          }
          accumulator[cfi].push(comment);
          return accumulator;
        }, {});

        setCommentsData((prev) => {
          const next = { ...prev };
          Object.entries(groupedByCfi).forEach(([cfi, comments]) => {
            next[cfi] = mergeCommentCollections(prev[cfi] || [], comments);
          });
          return next;
        });
      })
      .catch(console.error);
  }, [apiUrl, currentEpubId, currentUserId]);
  useEffect(() => {
    loadReaderCommentEntries();
  }, [loadReaderCommentEntries]);
  const readerAnnotationCount =
    readerHighlightEntries.length + readerCommentEntries.length;
  const sanitizeExportFilename = (value = "reader-comments") =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "reader-comments";
  const handleExportComments = () => {
      if (!readerCommentEntries.length) return;
      if (
        !window.confirm(
         "Would you like to export your comments as a text file to send to the author?"
        )
      ) {
        return;
      }

      const content = [...readerCommentEntries]
        .sort((a, b) => {
          const aDate = new Date(a.createdAt || 0).getTime();
          const bDate = new Date(b.createdAt || 0).getTime();
          return (aDate || 0) - (bDate || 0);
        })
        .map((item) => {
          const excerpt = item.excerpt ? `"${item.excerpt}"` : "";
          const comment = item.text ? `> ${item.text}` : "";
          return [excerpt, comment].filter(Boolean).join("\n");
        })
      .filter(Boolean)
      .join("\n\n");

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sanitizeExportFilename(epub?.title || "reader-comments")}-comments.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const hasAppearanceResetRisk = (theme) => {
    const preset = READER_THEME_PRESETS[theme] || READER_DEFAULT_APPEARANCE;
    return (
      readerAppearance.fontFamily !== preset.fontFamily ||
      readerAppearance.textSize !== preset.textSize ||
      readerAppearance.lineSpacing !== preset.lineSpacing
    );
  };
  const handleAppearanceThemeSelect = (theme) => {
    if (hasAppearanceResetRisk(theme)) {
      const confirmed = window.confirm(
        "Switching themes will reset your font, text size, and line spacing to that theme's defaults. Continue?"
      );
      if (!confirmed) return;
    }
    setReaderAppearance(normalizeReaderAppearance(READER_THEME_PRESETS[theme]));
    setIsFontMenuOpen(false);
  };
  const handleAppearanceTextSizeChange = (delta) => {
    setReaderAppearance((current) => ({
      ...current,
      textSize: Math.min(24, Math.max(12, current.textSize + delta))
    }));
  };
  const handleAppearanceFontFamilyChange = (fontFamily) => {
    setReaderAppearance((current) => ({
      ...current,
      fontFamily
    }));
    setIsFontMenuOpen(false);
  };
  const handleAppearanceLineSpacingChange = (value) => {
    const nextValue = Number(value);
    if (Number.isNaN(nextValue)) return;
    setReaderAppearance((current) => ({
      ...current,
      lineSpacing: Number(nextValue.toFixed(2))
    }));
  };
  const handleHighlightSwipeStart = (itemId, event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    highlightSwipeStateRef.current = {
      id: itemId,
      startX: touch.clientX,
      startY: touch.clientY,
      isSwiping: false
    };
    suppressHighlightOpenRef.current = false;
  };
  const handleHighlightSwipeMove = (itemId, event) => {
    const touch = event.touches?.[0];
    const swipeState = highlightSwipeStateRef.current;
    if (!touch || swipeState.id !== itemId) return;

    const deltaX = touch.clientX - swipeState.startX;
    const deltaY = touch.clientY - swipeState.startY;

    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      return;
    }

    if (deltaX <= -24) {
      swipeState.isSwiping = true;
      suppressHighlightOpenRef.current = true;
      setSwipedHighlightId(itemId);
    } else if (deltaX >= 24 && swipedHighlightId === itemId) {
      swipeState.isSwiping = true;
      suppressHighlightOpenRef.current = true;
      setSwipedHighlightId(null);
    }
  };
  const handleHighlightSwipeEnd = (itemId) => {
    const swipeState = highlightSwipeStateRef.current;
    if (swipeState.id === itemId && swipeState.isSwiping) {
      window.setTimeout(() => {
        suppressHighlightOpenRef.current = false;
      }, 120);
    } else {
      suppressHighlightOpenRef.current = false;
    }
    highlightSwipeStateRef.current = {
      id: null,
      startX: 0,
      startY: 0,
      isSwiping: false
    };
  };
  const handleHighlightItemOpen = (item) => {
    if (suppressHighlightOpenRef.current) return;
    if (swipedHighlightId === item.id) {
      setSwipedHighlightId(null);
      return;
    }
    openSearchResult(item);
  };
  const handleHighlightDelete = (item) => {
    setSwipedHighlightId(null);
    deleteHighlight(item.cfi, item.color);
  };
  const clearTempSearchHighlight = () => {
    clearTimeout(tempSearchHighlightTimerRef.current);
    try {
      if (
        tempSearchHighlightRef.current &&
        tempSearchHighlightIsOverlayRef.current &&
        renditionRef.current
      ) {
        renditionRef.current.annotations.remove(
          tempSearchHighlightRef.current,
          "highlight"
        );
      }
    } catch (e) {}
    tempSearchHighlightNodesRef.current.forEach(({ node, style }) => {
      if (!node?.style) return;
      node.style.fill = style.fill;
      node.style.fillOpacity = style.fillOpacity;
      node.style.mixBlendMode = style.mixBlendMode;
      node.style.pointerEvents = style.pointerEvents;
      node.style.transition = style.transition;
    });
    tempSearchHighlightNodesRef.current = [];
    tempSearchHighlightIsOverlayRef.current = false;
    tempSearchHighlightRef.current = null;
    tempSearchHighlightHrefRef.current = null;
  };
  const centerCfiInView = (cfi) => {
    const rendition = renditionRef.current;
    if (!rendition || !cfi) return;

    const attemptCenter = () => {
      try {
        const annotation = _getAnnotationRecord(rendition, cfi);
        const range = annotation?.mark?.range;
        const anchorEl =
          range?.startContainer?.nodeType === 1
            ? range.startContainer
            : range?.startContainer?.parentElement;
        const block =
          anchorEl?.closest?.("p, li, blockquote") ||
          anchorEl?.closest?.("div");
        if (block?.scrollIntoView) {
          block.scrollIntoView({ block: "center", inline: "nearest" });
          return true;
        }
      } catch (e) {}

      try {
        const el = _getAnnotationElement(rendition, cfi);
        if (el?.scrollIntoView) {
          el.scrollIntoView({ block: "center", inline: "nearest" });
          return true;
        }
      } catch (e) {}

      try {
        const annotation = _getAnnotationRecord(rendition, cfi);
        const rangeRect = annotation?.mark?.range?.getBoundingClientRect?.();
        if (rangeRect && rangeRect.height >= 0) {
          const doc = annotation.mark.range.startContainer?.ownerDocument;
          const win = doc?.defaultView;
          if (win) {
            const targetTop =
              win.scrollY + rangeRect.top - win.innerHeight / 2 + rangeRect.height / 2;
            win.scrollTo({ top: Math.max(0, targetTop), behavior: "auto" });
            return true;
          }
        }
      } catch (e) {}

      try {
        const el = _getAnnotationElement(rendition, cfi);
        const rect = el?.getBoundingClientRect?.();
        const win = el?.ownerDocument?.defaultView;
        if (rect && win) {
          const targetTop =
            win.scrollY + rect.top - win.innerHeight / 2 + rect.height / 2;
          win.scrollTo({ top: Math.max(0, targetTop), behavior: "auto" });
          return true;
        }
      } catch (e) {}

      return false;
    };

    [120, 320, 650, 950].forEach((delay) => {
      setTimeout(() => {
        attemptCenter();
      }, delay);
    });
  };
  const styleTempSearchHighlight = (cfi) => {
    const rendition = renditionRef.current;
    if (!rendition || !cfi) return;
    setTimeout(() => {
      try {
        const el = _getAnnotationElement(rendition, cfi);
        if (!el) return;
        const nodes = Array.from(el.querySelectorAll("rect, line, path"));
        tempSearchHighlightNodesRef.current = nodes.map((node) => ({
          node,
          style: {
            fill: node.style.fill || "",
            fillOpacity: node.style.fillOpacity || "",
            mixBlendMode: node.style.mixBlendMode || "",
            pointerEvents: node.style.pointerEvents || "",
            transition: node.style.transition || ""
          }
        }));
        nodes.forEach((node) => {
          node.style.transition = "fill 0.22s ease, fill-opacity 0.22s ease";
          node.style.fill = "#8fb7ff";
          node.style.fillOpacity =
            readerAppearanceRef.current.theme === "dark" ? "1" : "0.48";
          node.style.mixBlendMode = "multiply";
        });
      } catch (e) {}
    }, 80);
  };
  const flashSearchResult = (cfi) => {
    const rendition = renditionRef.current;
    if (!rendition || !cfi) return;

    clearTempSearchHighlight();
    tempSearchHighlightRef.current = cfi;
    try {
      const spineItem = rendition.book?.spine?.get(cfi);
      tempSearchHighlightHrefRef.current = spineItem?.href?.split("#")[0] || null;
    } catch (e) {
      tempSearchHighlightHrefRef.current = null;
    }

    const existingHighlight = highlightsRef.current.find((item) => item.cfi === cfi);
    if (existingHighlight) {
      tempSearchHighlightIsOverlayRef.current = false;
      styleTempSearchHighlight(cfi);
      centerCfiInView(cfi);
      tempSearchHighlightTimerRef.current = setTimeout(() => {
        if (tempSearchHighlightRef.current !== cfi) return;
        clearTempSearchHighlight();
      }, 2000);
      return;
    }

    tempSearchHighlightIsOverlayRef.current = true;
    try {
      rendition.annotations.add("highlight", cfi, {}, null, "", {
        fill: "#8fb7ff",
        "fill-opacity": getHighlightFillOpacity()
      });
      styleTempSearchHighlight(cfi);
      centerCfiInView(cfi);
      tempSearchHighlightTimerRef.current = setTimeout(() => {
        if (tempSearchHighlightRef.current !== cfi) return;
        clearTempSearchHighlight();
      }, 2000);
    } catch (e) {}
  };
  const openSearchResult = (result) => {
    const rendition = renditionRef.current;
    if (!rendition?.display || !result?.cfi) return;
    closeReaderTool();
    rendition.display(result.cfi).then(() => {
      flashSearchResult(result.cfi);
    }).catch(console.error);
  };
  const openCommentResult = (result) => {
    if (!result?.cfi) return;
    closeReaderTool();
    _loadCommentsApi(result.cfi, result.commentId || null);
  };
  const _commentAnchorStorageKey = () =>
    `reader-comment-anchors-${currentUserId}-${currentEpubId}`;
  const _commentExcerptStorageKey = () =>
    `reader-comment-excerpts-${currentUserId}-${currentEpubId}`;
  const _loadCommentAnchorStore = () => {
    try {
      return JSON.parse(localStorage.getItem(_commentAnchorStorageKey()) || "{}");
    } catch (e) {
      return {};
    }
  };
  const _saveCommentAnchorStore = (nextStore) => {
    try {
      localStorage.setItem(_commentAnchorStorageKey(), JSON.stringify(nextStore));
    } catch (e) {}
  };
  const _loadCommentExcerptStore = () => {
    try {
      return JSON.parse(localStorage.getItem(_commentExcerptStorageKey()) || "{}");
    } catch (e) {
      return {};
    }
  };
  const _saveCommentExcerptStore = (nextStore) => {
    try {
      localStorage.setItem(_commentExcerptStorageKey(), JSON.stringify(nextStore));
    } catch (e) {}
  };
  const _setCommentExcerpt = (commentId, excerpt) => {
    if (!commentId || !excerpt) return;
    const current = _loadCommentExcerptStore();
    _saveCommentExcerptStore({
      ...current,
      [commentId]: excerpt
    });
  };
  const _attachCommentExcerpts = (comments = [], fallbackExcerpt = "") => {
    const stored = _loadCommentExcerptStore();
    return comments.map((comment) => ({
      ...comment,
      selected_excerpt:
        comment.selected_excerpt ||
        stored[comment.id] ||
        fallbackExcerpt
    }));
  };
  const _setCommentAnchorMeta = (cfi, meta) => {
    const nextRef = {
      ...commentAnchorsRef.current,
      [cfi]: { ...(commentAnchorsRef.current[cfi] || {}), ...meta }
    };
    commentAnchorsRef.current = nextRef;
    _saveCommentAnchorStore(nextRef);
  };
  const _removeCommentAnchorMeta = (cfi) => {
    const nextRef = { ...commentAnchorsRef.current };
    delete nextRef[cfi];
    commentAnchorsRef.current = nextRef;
    _saveCommentAnchorStore(nextRef);
  };

  // ── Note dot helpers ───────────────────────────────────────────────────────

  const _getAnnotationElement = (rendition, cfi, type = "highlight") => {
    try {
      const hash = encodeURI(`${cfi}${type}`);
      const annotation = rendition.annotations?._annotations?.[hash];
      const element = annotation?.mark?.element;
      if (element) return element;
    } catch (e) {}
    try {
      const docs = new Set([document]);
      rendition.getContents().forEach((c) => docs.add(c.document));
      for (const doc of docs) {
        if (type === "mark") {
          for (const el of doc.querySelectorAll("[data-comment-cfi]")) {
            if (el.getAttribute("data-comment-cfi") === cfi) return el;
          }
        } else {
          for (const g of doc.querySelectorAll("g[data-epubcfi]")) {
            if (decodeURIComponent(g.getAttribute("data-epubcfi") || "") === cfi)
              return g;
          }
        }
      }
    } catch (e) {}
    return null;
  };

  const _getAnnotationRecord = (rendition, cfi, type = "highlight") => {
    try {
      const hash = encodeURI(`${cfi}${type}`);
      return rendition.annotations?._annotations?.[hash] || null;
    } catch (e) {
      return null;
    }
  };

  const _normalizeCommentText = (value = "") =>
    String(value)
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

  const _getCommentDisplayFingerprintFromRange = (range) => {
    const paragraph = _getParagraphAnchorFromRange(range);
    if (!paragraph) return "";
    return (paragraph.textContent || "").replace(/\s+/g, " ").trim().slice(0, 500);
  };

  const _getCommentAnchorExcerpt = (cfi) => {
    const directMetaText = commentAnchorsRef.current[cfi]?.highlighted_text || "";
    if (directMetaText.trim()) return directMetaText;

    const commentExcerpt = (commentsDataRef.current[cfi] || [])
      .map((comment) => comment?.selected_excerpt || "")
      .find((excerpt) => String(excerpt || "").trim().length >= 3);
    if (commentExcerpt) return commentExcerpt;

    const highlightExcerpt =
      highlightsRef.current.find((item) => item.cfi === cfi)?.highlighted_text || "";
    return highlightExcerpt;
  };

  const _getRangeStartRect = (range) => {
    if (!range?.getClientRects) return range?.getBoundingClientRect?.() || null;
    const rects = Array.from(range.getClientRects()).filter(
      (rect) => rect && rect.width > 0 && rect.height > 0
    );
    return rects[0] || range.getBoundingClientRect?.() || null;
  };

  const _getCommentBlockRect = (node) => {
    const rect = node?.getBoundingClientRect?.();
    if (!rect || rect.width <= 0 || rect.height <= 0) return null;
    return rect;
  };

  const _isCommentBlockElement = (node) => {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName?.toLowerCase?.() || "";
    if (["p", "li", "blockquote", "div", "section", "article"].includes(tag)) {
      return true;
    }
    const win = node.ownerDocument?.defaultView;
    const display = win?.getComputedStyle?.(node)?.display || "";
    return !!display && display !== "inline" && display !== "contents";
  };

  const _scoreCommentBlockCandidate = (node, targetRect, targetText = "") => {
    const rect = _getCommentBlockRect(node);
    const normalizedText = _normalizeCommentText(node?.textContent || "");
    if (!rect || !normalizedText) return null;

    const targetMid = targetRect.top + targetRect.height / 2;
    if (targetMid < rect.top - 2 || targetMid > rect.bottom + 2) return null;

    const normalizedTargetText = _normalizeCommentText(targetText);
    if (
      normalizedTargetText &&
      normalizedTargetText.length >= 6 &&
      !normalizedText.includes(normalizedTargetText)
    ) {
      return null;
    }

    const topDistance = Math.abs(targetRect.top - rect.top);
    const widthPenalty = rect.width < targetRect.width ? targetRect.width - rect.width : 0;
    const semanticBonus = /^(p|li|blockquote)$/.test(node.tagName?.toLowerCase?.() || "")
      ? -2000
      : 0;

    return {
      node,
      score:
        topDistance * 120 +
        rect.height * 8 +
        (rect.width * rect.height) / 40 +
        normalizedText.length +
        widthPenalty * 20 +
        semanticBonus
    };
  };

  const _findNestedCommentBlock = (rootNode, range) => {
    if (!rootNode || !range?.startContainer) return null;
    const targetRect = _getRangeStartRect(range);
    if (!targetRect) return null;

    const targetText = range.toString?.() || "";
    const candidates = Array.from(
      rootNode.querySelectorAll(COMMENT_BLOCK_SELECTOR)
    ).filter(
      (node) => node !== rootNode && !node.closest("svg") && _isCommentBlockElement(node)
    );

    const scored = candidates
      .map((node) => _scoreCommentBlockCandidate(node, targetRect, targetText))
      .filter(Boolean)
      .sort((a, b) => a.score - b.score);

    return scored[0]?.node || null;
  };

  const _getParagraphAnchorFromRange = (range) => {
    if (!range) return null;
    const targetRect = _getRangeStartRect(range);
    const candidates = [
      range.startContainer?.nodeType === 1
        ? range.startContainer
        : range.startContainer?.parentElement,
      range.commonAncestorContainer?.nodeType === 1
        ? range.commonAncestorContainer
        : range.commonAncestorContainer?.parentElement,
      range.endContainer?.nodeType === 1
        ? range.endContainer
        : range.endContainer?.parentElement
    ].filter(Boolean);

    for (const node of candidates) {
      if (!node.closest) continue;

      const semanticBlock = node.closest("p, li, blockquote");
      if (semanticBlock) return semanticBlock;

      let current = node;
      while (current && current.nodeType === 1) {
        if (_isCommentBlockElement(current)) {
          const rect = _getCommentBlockRect(current);
          if (
            rect &&
            targetRect &&
            Math.abs(targetRect.top - rect.top) <= 36 &&
            rect.height <= Math.max(targetRect.height * 8, 240)
          ) {
            return current;
          }
        }
        current = current.parentElement;
      }

      const coarseBlock = node.closest("div, section, article");
      const refinedBlock = _findNestedCommentBlock(coarseBlock, range);
      if (refinedBlock) return refinedBlock;
      if (coarseBlock) return coarseBlock;
    }

    return null;
  };

  const _findTextRangeWithinNode = (rootNode, targetText) => {
    if (!rootNode || !targetText) return null;
    const normalizedTarget = targetText.replace(/\s+/g, " ").trim();
    if (!normalizedTarget) return null;

    const doc = rootNode.ownerDocument || document;
    const walker = doc.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let fullText = "";
    let currentNode = walker.nextNode();

    while (currentNode) {
      const raw = currentNode.nodeValue || "";
      textNodes.push({
        node: currentNode,
        start: fullText.length,
        end: fullText.length + raw.length,
        text: raw
      });
      fullText += raw;
      currentNode = walker.nextNode();
    }

    if (!fullText.trim()) return null;

    let normalizedFullText = "";
    const normalizedIndexToOriginalIndex = [];
    let lastWasSpace = false;

    for (let i = 0; i < fullText.length; i += 1) {
      const ch = fullText[i];
      if (/\s/.test(ch)) {
        if (!lastWasSpace) {
          normalizedIndexToOriginalIndex.push(i);
          normalizedFullText += " ";
          lastWasSpace = true;
        }
      } else {
        normalizedIndexToOriginalIndex.push(i);
        normalizedFullText += ch;
        lastWasSpace = false;
      }
    }

    const compactIndex = normalizedFullText.indexOf(normalizedTarget);
    if (compactIndex === -1) return null;

    const originalStart = normalizedIndexToOriginalIndex[compactIndex];
    const normalizedEndIndex = compactIndex + normalizedTarget.length - 1;
    const originalEndCharIndex = normalizedIndexToOriginalIndex[normalizedEndIndex];
    if (originalStart == null || originalEndCharIndex == null) return null;

    const originalEnd = Math.min(fullText.length, originalEndCharIndex + 1);
    const startEntry = textNodes.find(
      (entry) => originalStart >= entry.start && originalStart <= entry.end
    );
    const endEntry = textNodes.find(
      (entry) => originalEnd >= entry.start && originalEnd <= entry.end
    );
    if (!startEntry || !endEntry) return null;

    const range = doc.createRange();
    range.setStart(startEntry.node, Math.max(0, originalStart - startEntry.start));
    range.setEnd(endEntry.node, Math.max(0, originalEnd - endEntry.start));
    return range;
  };

  const _getVisualSegmentTop = (containerNode, targetRange) => {
    if (!containerNode || !targetRange) return null;
    const doc = containerNode.ownerDocument || document;
    const paragraphRange = doc.createRange();
    paragraphRange.selectNodeContents(containerNode);
    const lineRects = Array.from(paragraphRange.getClientRects?.() || [])
      .filter((rect) => rect && rect.width > 0 && rect.height > 0)
      .map((rect) => ({
        top: rect.top,
        bottom: rect.bottom,
        height: rect.height,
        left: rect.left
      }))
      .sort((a, b) => {
        if (Math.abs(a.top - b.top) > 1) return a.top - b.top;
        return a.left - b.left;
      });
    if (!lineRects.length) return null;

    const targetRect = _getRangeStartRect(targetRange);
    if (!targetRect) return null;
    const targetMid = targetRect.top + targetRect.height / 2;

    let index = lineRects.findIndex(
      (line) => targetMid >= line.top - 1 && targetMid <= line.bottom + 1
    );
    if (index === -1) {
      index = lineRects.reduce(
        (best, line, lineIndex) => {
          const distance = Math.abs(line.top + line.height / 2 - targetMid);
          if (distance < best.distance) {
            return { index: lineIndex, distance };
          }
          return best;
        },
        { index: 0, distance: Math.abs(lineRects[0].top + lineRects[0].height / 2 - targetMid) }
      ).index;
    }

    let segmentStart = index;
    while (segmentStart > 0) {
      const current = lineRects[segmentStart];
      const previous = lineRects[segmentStart - 1];
      const gap = current.top - previous.bottom;
      const gapThreshold = Math.max(current.height, previous.height) * 0.9;
      if (gap > gapThreshold) break;
      segmentStart -= 1;
    }

    return lineRects[segmentStart]?.top ?? null;
  };

  const _getCommentVisualAnchorData = (range) => {
    if (!range) return null;
    const paragraph = _getParagraphAnchorFromRange(range);
    if (!paragraph) return null;
    const paragraphRect = paragraph?.getBoundingClientRect?.() || null;
    const rangeRect = _getRangeStartRect(range);
    if (!paragraphRect || !rangeRect) return null;

    const segmentTop = _getVisualSegmentTop(paragraph, range) ?? rangeRect.top;
    const offsetTop = segmentTop - paragraphRect.top;
    if (!Number.isFinite(offsetTop)) return null;

    return {
      offsetTop: Math.max(0, Math.round(offsetTop)),
      blockHeight: Math.max(0, Math.round(paragraphRect.height))
    };
  };

  const _getCommentNodeNeighborhood = (node) => {
    if (!node?.parentElement) return [];
    const siblings = Array.from(node.parentElement.children);
    const index = siblings.indexOf(node);
    const start = Math.max(0, index - 2);
    const end = Math.min(siblings.length, index + 3);
    return siblings.slice(start, end).map((sibling, offset) => ({
      index: start + offset,
      isTarget: sibling === node,
      ...summarizeCommentNode(sibling),
      rect: summarizeCommentRect(sibling.getBoundingClientRect?.())
    }));
  };

  const _findParagraphByDisplayFingerprint = (doc, fingerprint, range) => {
    if (!doc || !fingerprint) return null;

    const normalizedFingerprint = _normalizeCommentText(fingerprint);
    if (!normalizedFingerprint) return null;

    const rangeRect = range?.getBoundingClientRect?.() || null;
    const blocks = Array.from(doc.querySelectorAll(COMMENT_BLOCK_SELECTOR));
    const matchingBlocks = blocks.filter((node) => {
      const text = _normalizeCommentText(node.textContent || "");
      if (!text) return false;
      return (
        text === normalizedFingerprint ||
        text.includes(normalizedFingerprint) ||
        normalizedFingerprint.includes(text)
      );
    });

    if (!matchingBlocks.length) return null;
    if (!rangeRect) return matchingBlocks[0];

    const rangeMid = rangeRect.top + rangeRect.height / 2;
    return matchingBlocks
      .map((node) => {
        const rect = node.getBoundingClientRect?.();
        const distance = rect
          ? Math.abs(rect.top + rect.height / 2 - rangeMid)
          : Number.POSITIVE_INFINITY;
        const area = rect ? rect.width * rect.height : Number.POSITIVE_INFINITY;
        return { node, distance, area };
      })
      .sort((a, b) => {
        if (a.distance !== b.distance) return a.distance - b.distance;
        return a.area - b.area;
      })[0]?.node || matchingBlocks[0];
  };

  const _positionCommentMarker = (rendition, cfi) => {
    try {
      const marker = _getAnnotationElement(rendition, cfi, "mark");
      const annotation = _getAnnotationRecord(rendition, cfi, "mark");
      const range = annotation?.mark?.range;
      if (!marker || !range) return;

      const doc =
        range.startContainer?.ownerDocument ||
        range.commonAncestorContainer?.ownerDocument ||
        null;
      const savedVisualOffsetTop = Number(
        commentAnchorsRef.current[cfi]?.visual_offset_top
      );
      const fingerprint = commentAnchorsRef.current[cfi]?.display_paragraph_text || "";
      const excerpt = _getCommentAnchorExcerpt(cfi);
      const anchorBlock = _getParagraphAnchorFromRange(range);
      const paragraph =
        Number.isFinite(savedVisualOffsetTop)
          ? anchorBlock
          : _findParagraphByDisplayFingerprint(doc, fingerprint, range) || anchorBlock;
      const paragraphRect = paragraph?.getBoundingClientRect?.() || null;
      const matchedTextRange =
        _findTextRangeWithinNode(paragraph, excerpt) ||
        _findTextRangeWithinNode(doc?.body || null, excerpt);
      const rangeRect = _getRangeStartRect(matchedTextRange || range);
      const rect = paragraphRect || rangeRect || range.getBoundingClientRect();
      const iconSize = 20;
      const markerWidth = 30;
      const rightOffset = 6;
      const markerLeft = Math.max(0, rect.right + rightOffset);
      const visualSegmentTop = _getVisualSegmentTop(
        paragraph,
        matchedTextRange || range
      );
      const shouldUseRangeTop =
        !!paragraphRect &&
        !!rangeRect &&
        rangeRect.top - paragraphRect.top > 28;
      const savedMarkerTop =
        Number.isFinite(savedVisualOffsetTop) && paragraphRect
          ? Math.max(
              0,
              paragraphRect.top +
                Math.min(
                  Math.max(0, savedVisualOffsetTop),
                  Math.max(0, paragraphRect.height - iconSize / 2)
                )
            )
          : null;
      const markerTop = Math.max(
        0,
        savedMarkerTop != null
          ? savedMarkerTop
          : visualSegmentTop != null
          ? visualSegmentTop
          : shouldUseRangeTop
            ? rangeRect.top
            : rect.top
      );

      marker.style.top = `${markerTop}px`;
      marker.style.left = `${markerLeft}px`;
      marker.style.right = "auto";
      marker.style.width = `${markerWidth}px`;
      marker.style.height = `${iconSize}px`;
      marker.style.transform = "";

      debugReaderComment("position-marker", {
        cfi,
        fingerprint,
        rangeText: (range.toString?.() || "").replace(/\s+/g, " ").trim().slice(0, 180),
        rangeRect: summarizeCommentRect(rangeRect),
        paragraphTag: summarizeCommentNode(paragraph)?.tag || "",
        paragraphClass: summarizeCommentNode(paragraph)?.className || "",
        paragraphText: summarizeCommentNode(paragraph)?.text || "",
        paragraphRect: summarizeCommentRect(paragraphRect || rect),
        neighborhood: summarizeCommentNeighborhood(
          _getCommentNodeNeighborhood(paragraph)
        ),
        excerpt: (excerpt || "").slice(0, 180),
        matchFound: !!matchedTextRange,
        savedVisualOffsetTop: Number.isFinite(savedVisualOffsetTop)
          ? Math.round(savedVisualOffsetTop)
          : null,
        topSource: savedMarkerTop != null
          ? "saved-visual-offset"
          : visualSegmentTop != null
          ? "visual-segment-top"
          : matchedTextRange
            ? "matched-text-range"
          : shouldUseRangeTop
            ? "range-start"
            : "paragraph-top",
        visualSegmentTop:
          visualSegmentTop != null ? Math.round(visualSegmentTop) : null,
        markerTop: Math.round(markerTop),
        markerLeft: Math.round(markerLeft)
      });
    } catch (e) {
      debugReaderComment("position-marker:error", {
        cfi,
        message: e?.message || String(e),
        stack: e?.stack || ""
      });
    }
  };

  const _getCommentAnchorTarget = (contents, range, fallbackCfi) => {
    try {
      const paragraph = _getParagraphAnchorFromRange(range);
      debugReaderComment("resolve-anchor-target", {
        fallbackCfi,
        selectedText: (range?.toString?.() || "").replace(/\s+/g, " ").trim().slice(0, 180),
        rangeRect: summarizeCommentRect(range?.getBoundingClientRect?.()),
        paragraphTag: summarizeCommentNode(paragraph)?.tag || "",
        paragraphClass: summarizeCommentNode(paragraph)?.className || "",
        paragraphText: summarizeCommentNode(paragraph)?.text || "",
        paragraphRect: summarizeCommentRect(paragraph?.getBoundingClientRect?.()),
        neighborhood: summarizeCommentNeighborhood(
          _getCommentNodeNeighborhood(paragraph)
        )
      });
      if (!paragraph || !contents?.cfiFromNode) {
        return fallbackCfi;
      }
      const paragraphCfi = contents.cfiFromNode(paragraph);
      debugReaderComment("resolved-anchor-cfi", {
        fallbackCfi,
        paragraphCfi
      });
      return paragraphCfi;
    } catch (e) {
      return fallbackCfi;
    }
  };

  function _getCommentItemCount(comments = []) {
    return comments.reduce((total, comment) => {
      const replyCount = Array.isArray(comment.replies)
        ? comment.replies.length
        : Number(comment.reply_count || 0);
      return total + 1 + replyCount;
    }, 0);
  }

  const _isCommentOnlyAnchor = (cfi, highlight) => {
    const meta = commentAnchorsRef.current[cfi];
    if (meta?.commentOnly) return true;
    if (!highlight) return false;
    if (
      highlight.commentOnly ||
      highlight.comment_only ||
      highlight.is_comment_only ||
      highlight.isCommentOnly ||
      highlight.source === "comment" ||
      highlight.type === "comment"
    ) {
      return true;
    }
    return !highlight.note_text && !highlight.color && !!highlight.highlighted_text;
  };

  const _openManageSheet = (cfi) => {
    const h = highlightsRef.current.find((item) => item.cfi === cfi);
    if (!h) return false;
    const color = normalizeHighlightColor(h.color);
    if (h.note_text) {
      setNoteSheet({
        cfi,
        color,
        text: h.note_text || "",
        isEditing: true
      });
      setSelToolbar(null);
      closeManageSheet();
      return true;
    }
    setManageSheet({
      cfi,
      color,
      hasNote: !!h.note_text,
      noteText: h.note_text || "",
      highlightedText: h.highlighted_text || ""
    });
    setSelToolbar(null);
    closeNoteSheet();
    return true;
  };

  const _markAnnotationInteractive = (rendition, cfi) => {
    try {
      const el = _getAnnotationElement(rendition, cfi);
      if (!el) return false;
      const isDarkTheme = readerAppearanceRef.current.theme === "dark";
      const highlightOpacity = getHighlightFillOpacity();
      const nodes = [el, ...el.querySelectorAll("rect, line, circle, path")];
      nodes.forEach((node) => {
        node.setAttribute("data-highlight-cfi", cfi);
        node.style.pointerEvents = "auto";
        node.style.cursor = "pointer";
        if (isDarkTheme) {
          node.style.mixBlendMode = "screen";
          node.style.opacity = "1";
          node.style.fillOpacity = highlightOpacity;
          node.setAttribute("opacity", "1");
          node.setAttribute("fill-opacity", highlightOpacity);
        }
      });
      if (!isDarkTheme) {
        el.querySelectorAll("rect, line, path").forEach((node) => {
          node.style.mixBlendMode = "multiply";
        });
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const _insertNoteDot = (rendition, cfi, color) => {
    try {
      const g = _getAnnotationElement(rendition, cfi);
      if (!g) return;

      g.querySelector(".epub-note-dot")?.remove();

      const doc = g.ownerDocument;
      const ns = "http://www.w3.org/2000/svg";
      const appliedColor = normalizeHighlightColor(color);
      let leftX = Infinity;
      let topY = Infinity;

      try {
        const annotation = _getAnnotationRecord(rendition, cfi);
        const range = annotation?.mark?.range;
        const parentSvg = g.closest("svg");
        const svgRect = parentSvg?.getBoundingClientRect?.();
        const vb = parentSvg?.viewBox?.baseVal;
        const svgWidth =
          (vb && vb.width > 0
            ? vb.width
            : parseFloat(parentSvg?.getAttribute("width") || "0")) || 0;
        const svgHeight =
          (vb && vb.height > 0
            ? vb.height
            : parseFloat(parentSvg?.getAttribute("height") || "0")) || 0;

        if (range && svgRect && svgWidth && svgHeight) {
          const scaleX = svgWidth / Math.max(svgRect.width, 1);
          const scaleY = svgHeight / Math.max(svgRect.height, 1);
          const rangeRects = Array.from(range.getClientRects())
            .filter((rect) => rect.width > 0 && rect.height > 0)
            .sort((a, b) => {
              if (Math.abs(a.top - b.top) > 1) return a.top - b.top;
              return a.left - b.left;
            });

          const firstRect = rangeRects[0];
          if (firstRect) {
            leftX = (firstRect.left - svgRect.left) * scaleX;
            topY = (firstRect.top - svgRect.top) * scaleY;
          }
        }
      } catch (e) {}

      if (!Number.isFinite(leftX) || !Number.isFinite(topY)) {
        const rects = Array.from(g.querySelectorAll("rect"))
          .map((rect) => ({
            x: parseFloat(rect.getAttribute("x") || 0),
            y: parseFloat(rect.getAttribute("y") || 0)
          }))
          .sort((a, b) => {
            if (Math.abs(a.y - b.y) > 0.5) return a.y - b.y;
            return a.x - b.x;
          });
        const firstRect = rects[0];
        if (!firstRect) return;
        leftX = firstRect.x;
        topY = firstRect.y;
      }

      const isDarkTheme = readerAppearanceRef.current.theme === "dark";
      const shadow = doc.createElementNS(ns, "circle");
      shadow.setAttribute("class", "epub-note-dot-shadow");
      shadow.setAttribute("cx", String(leftX + 0.8));
      shadow.setAttribute("cy", String(topY + 1.2));
      shadow.setAttribute("r", "5.2");
      shadow.setAttribute(
        "fill",
        isDarkTheme ? "rgba(0, 0, 0, 0.38)" : "rgba(67, 15, 16, 0.22)"
      );
      shadow.setAttribute("pointer-events", "none");
      g.appendChild(shadow);

      const dot = doc.createElementNS(ns, "circle");
      dot.setAttribute("class", "epub-note-dot");
      dot.setAttribute("cx", String(leftX));
      dot.setAttribute("cy", String(topY));
      dot.setAttribute("r", "4.5");
      dot.setAttribute("fill", appliedColor);
      dot.setAttribute("stroke", isDarkTheme ? "#242424" : "#ffffff");
      dot.setAttribute("stroke-width", "2");
      dot.setAttribute("pointer-events", "none");
      g.appendChild(dot);
    } catch (e) {}
  };

  const _insertCommentDot = (rendition, cfi, commentCount) => {
    try {
      const g = _getAnnotationElement(rendition, cfi);
      if (!g) return;

      const existing = g.querySelector(".epub-comment-dot");
      if (existing) existing.remove();

      const rects = g.querySelectorAll("rect, line");
      if (!rects.length) return;

      const parentSvg = g.closest("svg");
      if (parentSvg) parentSvg.style.overflow = "visible";

      let topY = Infinity;
      rects.forEach((r) => {
        const y = parseFloat(r.getAttribute("y") || 0);
        if (y < topY) topY = y;
      });

      const doc = g.ownerDocument;
      const ns = "http://www.w3.org/2000/svg";

      let iconY = topY;
      try {
        const scrollTop = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
        for (const para of doc.querySelectorAll("p")) {
          const r = para.getBoundingClientRect();
          const paraTop = r.top + scrollTop;
          const paraBottom = r.bottom + scrollTop;
          if (paraTop <= topY + 8 && paraBottom > topY) {
            iconY = paraTop;
            break;
          }
        }
      } catch (e) {}

      // Right-margin X from SVG or document width
      let svgRightEdge = 0;
      if (parentSvg) {
        const vb = parentSvg.viewBox?.baseVal;
        if (vb && vb.width > 0) svgRightEdge = vb.width;
        else svgRightEdge = parseFloat(parentSvg.getAttribute("width") || "0");
      }
      if (!svgRightEdge) {
        svgRightEdge = doc.body?.clientWidth || doc.documentElement?.clientWidth || 400;
      }

      const ICON_SIZE = 20;
      const scale = ICON_SIZE / 24;
      const iconX = svgRightEdge - ICON_SIZE - 10;

      const g_comment = doc.createElementNS(ns, "g");
      g_comment.setAttribute("class", "epub-comment-dot");
      g_comment.setAttribute("data-comment-cfi", cfi);
      g_comment.setAttribute("data-is-comment-icon", "true");
      g_comment.style.cursor = "pointer";
      g_comment.style.pointerEvents = "auto";

      // Pure SVG chat-bubble icon — no font dependency
      const g_icon = doc.createElementNS(ns, "g");
      g_icon.setAttribute("transform", `translate(${iconX}, ${iconY}) scale(${scale})`);
      g_icon.style.pointerEvents = "none";

      const commentMarkerColor = getCommentMarkerColor();
      const commentMarkerStroke = getCommentMarkerStroke();
      const bubble = doc.createElementNS(ns, "path");
      // Material Design "chat" 24px path
      bubble.setAttribute("d", "M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z");
      bubble.setAttribute("fill", commentMarkerColor);
      g_icon.appendChild(bubble);

      // Small unread-dot in top-right of bubble
      const dot = doc.createElementNS(ns, "circle");
      dot.setAttribute("cx", "20");
      dot.setAttribute("cy", "4");
      dot.setAttribute("r", "4");
      dot.setAttribute("fill", commentMarkerColor);
      dot.setAttribute("stroke", commentMarkerStroke);
      dot.setAttribute("stroke-width", "2");
      g_icon.appendChild(dot);

      g_comment.appendChild(g_icon);
      g.appendChild(g_comment);

      // Disable interactivity on the highlight rects so tapping text doesn't trigger anything
      g.querySelectorAll("rect, line").forEach((r) => {
        r.classList.add("epub-comment-tracking-rect");
        r.removeAttribute("data-highlight-cfi");
      });

      g_comment.addEventListener("click", (e) => {
        e.stopPropagation();
        _loadCommentsApi(cfi);
      });
    } catch (e) {}
  };

  const _addCommentDotDelayed = (rendition, cfi, commentCount) => {
    [100, 500].forEach((delay) =>
      setTimeout(() => {
        try {
          const g = _getAnnotationElement(rendition, cfi);
          if (g) {
            g.querySelectorAll("rect").forEach((rect) => {
              rect.setAttribute("fill-opacity", "0");
            });
          }
        } catch (e) {}
        _insertCommentDot(rendition, cfi, commentCount);
      }, delay)
    );
  };

  const _decorateCommentMarker = (marker, commentCount = 0) => {
    if (!marker) return;

    const currentCount = Number(marker.getAttribute("data-comment-count") || "0");
    if (currentCount === Number(commentCount) && marker.childNodes.length > 0) {
      return;
    }

    marker.setAttribute("data-is-comment-icon", "true");
    marker.setAttribute("data-comment-icon", "true");
    marker.setAttribute("data-comment-count", String(commentCount));
    marker.setAttribute("role", "button");
    marker.setAttribute("aria-label", "Open comments");
    marker.classList.add("epub-comment-anchor");
    marker.style.pointerEvents = "auto";
    marker.style.cursor = "pointer";
    marker.style.position = "absolute";

    marker.textContent = "";

    const doc = marker.ownerDocument;
    const ns = "http://www.w3.org/2000/svg";
    const svg = doc.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "20");
    svg.setAttribute("height", "20");
    svg.setAttribute("aria-hidden", "true");
    svg.style.pointerEvents = "none";

    const commentMarkerColor = getCommentMarkerColor();
    const commentMarkerStroke = getCommentMarkerStroke();
    const bubble = doc.createElementNS(ns, "path");
    bubble.setAttribute(
      "d",
      "M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
    );
    bubble.setAttribute("fill", commentMarkerColor);
    svg.appendChild(bubble);

    const dot = doc.createElementNS(ns, "circle");
    dot.setAttribute("cx", "19");
    dot.setAttribute("cy", "5");
    dot.setAttribute("r", "4");
    dot.setAttribute("fill", commentMarkerColor);
    dot.setAttribute("stroke", commentMarkerStroke);
    dot.setAttribute("stroke-width", "2");
    if (Number(commentCount) <= 1) {
      dot.setAttribute("opacity", "0");
    }
    svg.appendChild(dot);

    marker.appendChild(svg);
  };

  const _addOverlayCommentMarker = (rendition, cfi, commentCount) => {
    try {
      rendition.annotations.remove(cfi, "mark");
    } catch (e) {}

    try {
      rendition.annotations.add(
        "mark",
        cfi,
        { commentIcon: "true", commentCfi: cfi },
        (e) => {
          if (e?.preventDefault) e.preventDefault();
          if (e?.stopPropagation) e.stopPropagation();
          _loadCommentsApi(cfi);
        }
      );
    } catch (e) {}

    [0, 100, 300].forEach((delay) =>
      setTimeout(() => {
        const marker = _getAnnotationElement(rendition, cfi, "mark");
        if (!marker) return;
        marker.setAttribute("data-comment-cfi", cfi);
        _decorateCommentMarker(marker, commentCount);
        _positionCommentMarker(rendition, cfi);
      }, delay)
    );
  };

  const _upsertCommentMarker = (rendition, cfi, commentCount) => {
    _removeCommentMarker(rendition, cfi);
    try {
      rendition.annotations.remove(cfi, "mark");
    } catch (e) {}

    try {
      rendition.annotations.add(
        "mark",
        cfi,
        { commentIcon: "true", commentCfi: cfi },
        (e) => {
          if (e?.preventDefault) e.preventDefault();
          if (e?.stopPropagation) e.stopPropagation();
          _loadCommentsApi(cfi);
        }
      );
    } catch (e) {}

    [0, 100, 300].forEach((delay) =>
      setTimeout(() => {
        const marker = _getAnnotationElement(rendition, cfi, "mark");
        if (!marker) return;
        marker.setAttribute("data-comment-cfi", cfi);
        _decorateCommentMarker(marker, commentCount);
        _positionCommentMarker(rendition, cfi);
      }, delay)
    );
  };

  const _removeCommentMarker = (rendition, cfi) => {
    try {
      rendition?.annotations?.remove(cfi, "mark");
    } catch (e) {}

    try {
      const docs = new Set([document]);
      rendition?.getContents?.().forEach((content) => docs.add(content.document));
      docs.forEach((doc) => {
        doc.querySelectorAll("[data-comment-cfi]").forEach((node) => {
          if (node.getAttribute("data-comment-cfi") === cfi) {
            node.remove();
          }
        });
      });
    } catch (e) {}
  };

  const _addNoteDotDelayed = (rendition, cfi, color) => {
    [500, 1200].forEach((delay) =>
      setTimeout(() => _insertNoteDot(rendition, cfi, color), delay)
    );
  };

  // ── Highlight helpers ──────────────────────────────────────────────────────

  const _applyToRendition = (
    rendition,
    cfi,
    color,
    hasNote = false
  ) => {
    const appliedColor = normalizeHighlightColor(color);
    try {
      rendition.annotations.remove(cfi, "highlight");
    } catch (e) {}
    try {
      rendition.annotations.remove(cfi, "underline");
    } catch (e) {}

    // Desktop: per-annotation callback (works via SVG click event)
    const onMarkClick = (e) => {
      if (e && e.stopPropagation) e.stopPropagation();
      _openManageSheet(cfi);
    };

    try {
      rendition.annotations.add("highlight", cfi, {}, onMarkClick, "", {
        fill: appliedColor,
        "fill-opacity": getHighlightFillOpacity()
      });
    } catch (e) {}

    [0, 150, 500].forEach((delay) => {
      setTimeout(() => _markAnnotationInteractive(rendition, cfi), delay);
    });

    if (hasNote) _addNoteDotDelayed(rendition, cfi, appliedColor);
  };

  const _saveHighlightApi = (
    cfi,
    color,
    noteText,
    highlightedText,
    commentOnly = false
  ) => {
    const form = new FormData();
    form.append("uid", currentUserId);
    form.append("epub_id", id);
    form.append("cfi", cfi);
    form.append("color", color);
    form.append("note_text", noteText);
    form.append("highlighted_text", highlightedText);
    if (commentOnly) {
      form.append("comment_only", "1");
    }
    if (!navigator.onLine) {
      enqueueOfflineMutation(
        "upsert-highlight",
        {
          cfi,
          color,
          noteText,
          highlightedText,
          commentOnly
        },
        `highlight-${currentUserId}-${currentEpubId}-${cfi}`
      );
      return;
    }
    apiFetch(`${apiUrl}/add-highlight.php`, {
      method: "POST",
      body: form
    }).catch(console.error);
  };

  const applyHighlight = (cfi, color, noteText = "", highlightedText = "") => {
    const appliedColor = normalizeHighlightColor(color);
    if (commentAnchorsRef.current[cfi]?.commentOnly) {
      _removeCommentAnchorMeta(cfi);
    }
    _applyToRendition(
      renditionRef.current,
      cfi,
      appliedColor,
      noteText.length > 0
    );
    lastUsedColorRef.current = appliedColor;
    localStorage.setItem("highlight-last-color", appliedColor);
    try {
      contentsRef.current?.window.getSelection().removeAllRanges();
    } catch (e) {}
    const newH = {
      cfi,
      color: appliedColor,
      note_text: noteText,
      highlighted_text: highlightedText
    };
    syncReaderHighlights([
      ...highlightsRef.current.filter((h) => h.cfi !== cfi),
      newH
    ]);
    _saveHighlightApi(cfi, appliedColor, noteText, highlightedText);
  };

  const changeHighlightColor = (cfi, newColor) => {
    const h = highlightsRef.current.find((h) => h.cfi === cfi);
    applyHighlight(
      cfi,
      newColor,
      h?.note_text || "",
      h?.highlighted_text || ""
    );
    setManageSheet((prev) => (prev ? { ...prev, color: newColor } : null));
  };

  const deleteHighlight = (cfi, color) => {
    const existingHighlight = highlightsRef.current.find((h) => h.cfi === cfi);
    const hasComments =
      (commentsDataRef.current[cfi] || []).length > 0 ||
      !!commentAnchorsRef.current[cfi];

    try {
      renditionRef.current?.annotations.remove(cfi, "highlight");
      renditionRef.current?.annotations.remove(cfi, "underline");
    } catch (e) {}
    syncReaderHighlights(
      highlightsRef.current.filter((h) => h.cfi !== cfi)
    );

    if (hasComments && existingHighlight?.highlighted_text) {
      _setCommentAnchorMeta(cfi, {
        highlighted_text: existingHighlight.highlighted_text,
        commentOnly: true,
        persisted: true
      });
      _upsertCommentMarker(
        renditionRef.current,
        cfi,
        (commentsDataRef.current[cfi] || []).length || 1
      );
    }

    setManageSheet(null);
    setNoteSheet(null);
    const form = new FormData();
    form.append("uid", currentUserId);
    form.append("epub_id", id);
    form.append("cfi", cfi);
    if (!navigator.onLine) {
      enqueueOfflineMutation(
        "delete-highlight",
        { cfi },
        `highlight-${currentUserId}-${currentEpubId}-${cfi}`
      );
      return;
    }
    apiFetch(`${apiUrl}/delete-highlight.php`, {
      method: "POST",
      body: form
    }).catch(console.error);
  };

  // ── Toolbar / sheet handlers ───────────────────────────────────────────────

  const handleHighlightFromSelection = () => {
    if (!selToolbar) return;
    applyHighlight(
      selToolbar.cfi,
      lastUsedColorRef.current,
      "",
      selToolbar.text
    );
    setSelToolbar(null);
  };

  const handleNoteFromSelection = () => {
    if (!selToolbar) return;
    const color = lastUsedColorRef.current;
    applyHighlight(selToolbar.cfi, color, "", selToolbar.text);
    setNoteSheet({ cfi: selToolbar.cfi, color, text: "", isEditing: false });
    setSelToolbar(null);
  };

  const handleCommentFromSelection = () => {
    if (!selToolbar) return;
    setAddCommentSheet({
      cfi: selToolbar.cfi,
      anchorCfi: selToolbar.commentAnchorCfi || selToolbar.cfi,
      displayParagraphText: selToolbar.commentDisplayParagraphText || "",
      visualOffsetTop: selToolbar.commentVisualOffsetTop ?? null,
      selectedText: selToolbar.text,
      text: ""
    });
    setSelToolbar(null);
  };

  const handleSendComment = (text) => {
    if (!addCommentSheet || !text.trim()) return;
    const {
      anchorCfi,
      selectedText,
      displayParagraphText,
      visualOffsetTop
    } = addCommentSheet;
    const targetCfi = anchorCfi || addCommentSheet.cfi;
    debugReaderComment("send-comment", {
      selectionCfi: addCommentSheet.cfi,
      targetCfi,
      selectedText: (selectedText || "").replace(/\s+/g, " ").trim().slice(0, 180),
      displayParagraphText: (displayParagraphText || "").slice(0, 180),
      visualOffsetTop: Number.isFinite(Number(visualOffsetTop))
        ? Number(visualOffsetTop)
        : null,
      existingMeta: commentAnchorsRef.current[targetCfi] || null
    });

    const existingH = highlightsRef.current.find((h) => h.cfi === targetCfi);
    if (!existingH) {
      const color = getHighlightPalette()[0];
      const nextHighlightedText = selectedText || "";
      const nextDisplayParagraphText = displayParagraphText || "";
      _setCommentAnchorMeta(targetCfi, {
        display_paragraph_text: nextDisplayParagraphText,
        highlighted_text: nextHighlightedText,
        visual_offset_top: Number.isFinite(Number(visualOffsetTop))
          ? Number(visualOffsetTop)
          : null,
        commentOnly: true,
        persisted: true
      });
      _upsertCommentMarker(renditionRef.current, targetCfi, 1);
      _saveHighlightApi(
        targetCfi,
        color,
        "",
        nextHighlightedText,
        true
      );
    } else {
      _setCommentAnchorMeta(targetCfi, {
        display_paragraph_text: displayParagraphText || "",
        highlighted_text: selectedText || "",
        visual_offset_top: Number.isFinite(Number(visualOffsetTop))
          ? Number(visualOffsetTop)
          : null
      });
      _upsertCommentMarker(
        renditionRef.current,
        targetCfi,
        (commentsDataRef.current[targetCfi] || []).length + 1
      );
    }

    _saveCommentApi(targetCfi, text.trim(), selectedText);
    closeAddCommentSheet();
  };

  const handleSaveNote = (text) => {
    if (!noteSheet) return;
    const h = highlightsRef.current.find((h) => h.cfi === noteSheet.cfi);
    applyHighlight(
      noteSheet.cfi,
      noteSheet.color,
      text,
      h?.highlighted_text || ""
    );

    closeNoteSheet();
    closeManageSheet();
  };

  // ── Comment helpers ────────────────────────────────────────────────────────

  const _saveCommentApi = (cfi, text, selectedText = "") => {
    const chapterTitle =
      resolveChapterTitle(
        renditionRef.current?.book,
        renditionRef.current?.currentLocation?.(),
        cfi
      ) ||
      latestProgressRef.current?.chapterTitle ||
      "";
    if (!navigator.onLine) {
      enqueueOfflineMutation("create-comment", {
        cfi,
        text,
        selectedText,
        chapterTitle
      });
      return;
    }

    const form = new FormData();
    form.append("uid", currentUserId);
    form.append("epub_id", id);
    form.append("cfi", cfi);
    form.append("text", text);
    form.append("selected_excerpt", selectedText);
    form.append("chapter_title", chapterTitle);
    apiFetch(`${apiUrl}/create-comment.php`, {
      method: "POST",
      body: form
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          const nextComment = {
            ...data.comment,
            selected_excerpt: selectedText || data.comment?.selected_excerpt || ""
          };
          if (nextComment.id && nextComment.selected_excerpt) {
            _setCommentExcerpt(nextComment.id, nextComment.selected_excerpt);
          }
          // Add new comment to commentsData
          setCommentsData((prev) => ({
            ...prev,
            [cfi]: [...(prev[cfi] || []), nextComment]
          }));
        }
      })
      .catch(console.error);
  };

  const _loadCommentsApi = (cfi, focusCommentId = null) => {
    // Find the highlight to get the highlighted text
    const highlight = highlightsRef.current.find((h) => h.cfi === cfi);
    const highlightedText =
      highlight?.highlighted_text ||
      commentAnchorsRef.current[cfi]?.highlighted_text ||
      "";
    const cachedComments = commentsDataRef.current[cfi] || [];

    if (cachedComments.length > 0) {
      setIsCommentsSheetClosing(false);
      setCommentsSheet({
        cfi,
        comments: cachedComments,
        highlightedText,
        focusCommentId,
        offlineOnly: false
      });
    }

    if (!navigator.onLine) {
      setIsCommentsSheetClosing(false);
      setCommentsSheet({
        cfi,
        comments: cachedComments,
        highlightedText,
        focusCommentId,
        offlineOnly: true
      });
      return;
    }

    apiFetch(
      `${apiUrl}/get-comments.php?epub_id=${id}&cfi=${encodeURIComponent(cfi)}&uid=${currentUserId}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          const commentsWithExcerpts = _attachCommentExcerpts(
            data.comments,
            highlightedText
          );
          setCommentsData((prev) => ({
            ...prev,
            [cfi]: commentsWithExcerpts
          }));
          setIsCommentsSheetClosing(false);
          setCommentsSheet({
            cfi,
            comments: commentsWithExcerpts,
            highlightedText,
            focusCommentId,
            offlineOnly: false
          });
        }
      })
      .catch((error) => {
        console.error(error);
        if (!navigator.onLine) {
          setCommentsSheet({
            cfi,
            comments: [],
            highlightedText,
            focusCommentId,
            offlineOnly: true
          });
        }
      });
  };

  const openPendingNotificationComment = useCallback(() => {
    const target = notificationCommentTargetRef.current;
    const rendition = renditionRef.current;

    if (!target?.cfi || !target?.commentId || !rendition) return;

    notificationCommentTargetRef.current = null;
    rendition
      .display(target.cfi)
      .then(() => {
        _loadCommentsApi(target.cfi, target.commentId);
      })
      .catch(console.error);
  }, [apiUrl, currentUserId, epubData, id]);

  useEffect(() => {
    if (!epubData) return;
    openPendingNotificationComment();
  }, [epubData, openPendingNotificationComment]);

  useEffect(() => {
    if (!commentsSheet?.cfi || !notificationProgressGuardRef.current || notificationProgressPromptedRef.current) {
      return undefined;
    }

    notificationProgressPromptedRef.current = true;
    clearTimeout(notificationPromptTimerRef.current);
    notificationPromptTimerRef.current = setTimeout(() => {
      const shouldUpdateProgress = window.confirm("Update your reading position?");
      if (shouldUpdateProgress) {
        notificationProgressGuardRef.current = false;
        if (latestProgressRef.current) {
          saveReadingProgress(latestProgressRef.current, true);
        }
        return;
      }

      notificationProgressGuardRef.current = true;
    }, 5000);

    return () => {
      clearTimeout(notificationPromptTimerRef.current);
    };
  }, [commentsSheet?.cfi]);

  useEffect(() => {
    if (commentsSheet) return;

    clearTimeout(notificationPromptTimerRef.current);
    notificationProgressPromptedRef.current = false;
    notificationProgressGuardRef.current = false;
  }, [commentsSheet]);

  const _handleCommentDeleted = (commentId) => {
    if (!commentsSheet) return;
    const cfi = commentsSheet.cfi;
    const updatedComments = (commentsData[cfi] || []).filter((c) => c.id !== commentId);

    setCommentsData((prev) => ({
      ...prev,
      [cfi]: updatedComments
    }));

    if (updatedComments.length === 0) {
      _removeCommentMarker(renditionRef.current, cfi);

      syncReaderHighlights(
        highlightsRef.current.filter(
          (h) => !(h.cfi === cfi && _isCommentOnlyAnchor(cfi, h))
        )
      );

      if (commentAnchorsRef.current[cfi]) {
        _removeCommentAnchorMeta(cfi);
        const form = new FormData();
        form.append("uid", currentUserId);
        form.append("epub_id", id);
        form.append("cfi", cfi);
        apiFetch(`${apiUrl}/delete-highlight.php`, {
          method: "POST",
          body: form
        }).catch(console.error);
      }

      closeCommentsSheet();
    }
  };

  const _handleReplyAdded = (commentId, reply) => {
    if (!commentsSheet) return;
    const cfi = commentsSheet.cfi;
    setCommentsData((prev) => ({
      ...prev,
      [cfi]: prev[cfi].map((c) =>
        c.id === commentId
          ? {
              ...c,
              replies: [...(c.replies || []), reply],
              reply_count: c.reply_count + 1
            }
          : c
      )
    }));
  };

  const _handleCommentLiked = (commentId, liked, likeCount) => {
    if (!commentsSheet) return;
    const cfi = commentsSheet.cfi;
    setCommentsData((prev) => ({
      ...prev,
      [cfi]: prev[cfi].map((c) =>
        c.id === commentId
          ? { ...c, user_liked: liked, like_count: likeCount }
          : c
      )
    }));
  };

  const _loadAndApplyCommentCounts = (rendition) => {
    if (!navigator.onLine) return Promise.resolve();

    const ownTargets = [
      ...highlightsRef.current.map((h) => ({
        cfi: h.cfi,
        highlighted_text: h.highlighted_text || ""
      })),
      ...Object.entries(commentAnchorsRef.current).map(([cfi, meta]) => ({
        cfi,
        highlighted_text: meta.highlighted_text || ""
      }))
    ];
    const ownCfis = new Set(ownTargets.map((t) => t.cfi));

    return apiFetch(`${apiUrl}/get-comment-cfis.php?epub_id=${id}&uid=${currentUserId}`)
      .then((r) => r.json())
      .then((cfiData) => {
        const publicTargets = cfiData.success
          ? cfiData.cfis
              .filter((item) => !ownCfis.has(item.cfi))
              .map((item) => ({
                cfi: item.cfi,
                highlighted_text: item.highlighted_text || "",
                comment_count: Number(item.comment_count || 0)
              }))
          : [];
        const serverPublicCfis = new Set(publicTargets.map((item) => item.cfi));
        const serverCounts = new Map(
          (Array.isArray(cfiData?.cfis) ? cfiData.cfis : []).map((item) => [
            item.cfi,
            Number(item.comment_count || 0)
          ])
        );

        Object.keys(commentAnchorsRef.current).forEach((cfi) => {
          const hasOwnHighlight = ownCfis.has(cfi);
          const isServerPublic = serverPublicCfis.has(cfi);
          const isCommentOnly = !!commentAnchorsRef.current[cfi]?.commentOnly;
          if (!hasOwnHighlight && !isServerPublic && isCommentOnly) {
            _removeCommentMarker(rendition, cfi);
            _removeCommentAnchorMeta(cfi);
            setCommentsData((prev) => {
              if (!(cfi in prev)) return prev;
              const next = { ...prev };
              delete next[cfi];
              return next;
            });
          }
        });

        const targets = [
          ...ownTargets.filter(
            (item, index, arr) =>
              arr.findIndex((entry) => entry.cfi === item.cfi) === index
          ),
          ...publicTargets
        ];

        targets.forEach((item) => {
          const count = serverCounts.get(item.cfi) || 0;

          if (count > 0) {
            if (!highlightsRef.current.find((h) => h.cfi === item.cfi)) {
              _setCommentAnchorMeta(item.cfi, {
                highlighted_text:
                  commentAnchorsRef.current[item.cfi]?.highlighted_text ||
                  item.highlighted_text ||
                  "",
                commentOnly: true,
                persisted: true
              });
            }
            _upsertCommentMarker(rendition, item.cfi, count);
            return;
          }

          _removeCommentMarker(rendition, item.cfi);
          setCommentsData((prev) => {
            if (!(item.cfi in prev)) return prev;
            const next = { ...prev };
            delete next[item.cfi];
            return next;
          });
          if (
            commentAnchorsRef.current[item.cfi]?.commentOnly &&
            !highlightsRef.current.find((h) => h.cfi === item.cfi)
          ) {
            _removeCommentAnchorMeta(item.cfi);
          }
        });
      })
      .catch(console.error);
  };

  const _refreshCommentsSync = () => {
    if (!navigator.onLine || document.visibilityState === "hidden") {
      return;
    }

    const rendition = renditionRef.current;
    if (!rendition) return;

    _loadAndApplyCommentCounts(rendition);

    const activeSheet = commentsSheetRef.current;
    if (activeSheet?.cfi) {
      _loadCommentsApi(activeSheet.cfi);
    }
  };

  useEffect(() => {
    if (!id) return;

    const refreshNow = () => _refreshCommentsSync();
    const refreshOnVisible = () => {
      if (document.visibilityState === "visible") {
        refreshNow();
      }
    };

    const intervalId = window.setInterval(refreshNow, 12000);

    window.addEventListener("focus", refreshNow);
    window.addEventListener("online", refreshNow);
    document.addEventListener("visibilitychange", refreshOnVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshNow);
      window.removeEventListener("online", refreshNow);
      document.removeEventListener("visibilitychange", refreshOnVisible);
    };
  }, [id]);

  // ── epub.js location helpers ───────────────────────────────────────────────

  useEffect(() => {
    if (!isOnline) return;
    flushOfflineQueue();
  }, [flushOfflineQueue, isOnline]);

  useEffect(() => {
    const flushOnHidden = () => {
      if (document.visibilityState === "hidden") {
        flushReadingProgress();
      }
    };
    const flushOnPageHide = () => {
      flushReadingProgress();
    };

    document.addEventListener("visibilitychange", flushOnHidden);
    window.addEventListener("pagehide", flushOnPageHide);
    window.addEventListener("beforeunload", flushOnPageHide);

    return () => {
      document.removeEventListener("visibilitychange", flushOnHidden);
      window.removeEventListener("pagehide", flushOnPageHide);
      window.removeEventListener("beforeunload", flushOnPageHide);
    };
  }, []);

  const getProgressLocationCacheKey = () => `epub-progress-locs-${id}-v1`;
  const findLocationIndexInMap = (cfi, locations) => {
    if (!cfi || !locations.length) return null;
    let low = 0;
    let high = locations.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      const comparison = progressEpubCfiRef.current.compare(cfi, locations[mid]);
      if (comparison > 0) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    if (low >= locations.length) {
      return locations.length - 1;
    }
    return low;
  };
  const getStableProgressPercent = (cfi, fallbackPercent = 0) => {
    const locations = progressLocationsRef.current;
    if (!locations.length || !cfi) return fallbackPercent;
    const loc = findLocationIndexInMap(cfi, locations);
    const total = locations.length - 1;
    if (loc == null || total <= 0) return fallbackPercent;
    return Math.round((loc / total) * 1000) / 10;
  };
  const getStablePageNumber = (cfi, fallbackPage = null) => {
    const locations = progressLocationsRef.current;
    if (!cfi || !locations.length) return fallbackPage;
    const loc = findLocationIndexInMap(cfi, locations);
    if (loc == null) return fallbackPage;
    return Math.max(1, loc + 1);
  };
  const prepareProgressLocations = async (book) => {
    const cacheKey = getProgressLocationCacheKey();
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          progressLocationsRef.current = parsed;
          progressLocationsReadyRef.current = true;
          return;
        }
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    }

    try {
      await book.locations.generate(READER_PROGRESS_BREAK);
      const saved = book.locations.save();
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length) {
        progressLocationsRef.current = parsed;
        progressLocationsReadyRef.current = true;
        localStorage.setItem(cacheKey, saved);
      }
    } catch (e) {}
  };

  const computeChapterEndPages = (book) => {
    const locations = progressLocationsRef.current;
    if (!book || !locations.length) {
      chapterEndPagesRef.current = {};
      return;
    }

    const nextChapterEnds = {};
    locations.forEach((cfi, index) => {
      try {
        const spineItem = book.spine.get(cfi);
        if (spineItem?.index != null) {
          nextChapterEnds[spineItem.index] = index + 1;
        }
      } catch (e) {}
    });
    chapterEndPagesRef.current = nextChapterEnds;
  };

  const getLocationCacheKey = (appearance) => {
    const viewportWidth = Math.max(window.innerWidth || 0, 320);
    const viewportHeight = Math.max(window.innerHeight || 0, 568);
    return [
      "epub-locs",
      id,
      appearance.fontFamily,
      appearance.textSize,
      appearance.lineSpacing.toFixed(2),
      viewportWidth,
      viewportHeight,
      "v3"
    ].join("-");
  };

  const applyLocations = (rendition, appearance) => {
    const cacheKey = getLocationCacheKey(appearance);
    const cached = localStorage.getItem(cacheKey);
    computeChapterEndPages(rendition.book);

    const setPageFromPosition = () => {
      try {
        const loc = rendition.currentLocation();
        const cfi = loc?.start?.cfi;
        const page = isValidCfi(cfi)
          ? getStablePageNumber(cfi, loc?.start?.location != null ? loc.start.location + 1 : null)
          : loc?.start?.location != null
            ? loc.start.location + 1
            : null;
        if (page != null) {
          setCurrentPage(page);
          return;
        }
        const target = isValidCfi(cfi) ? cfi : locationRef.current;
        if (isValidCfi(target)) {
          const stablePage = getStablePageNumber(target, null);
          if (stablePage != null) setCurrentPage(stablePage);
        }
      } catch (e) {}
    };

    if (cached) {
      try {
        rendition.book.locations.load(cached);
        setTotalPages(
          progressLocationsRef.current.length || rendition.book.locations.total || null
        );
        setTimeout(setPageFromPosition, 150);
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    } else {
      const viewportWidth = Math.max(window.innerWidth || 0, 320);
      const widthScale = Math.max(0.58, Math.min(1.18, viewportWidth / 430));
      const spacingScale = Math.max(0.8, Math.min(1.2, 1.5 / appearance.lineSpacing));
      const charPerPage = Math.round(
        1600 * (16 / appearance.textSize) * widthScale * spacingScale
      );
      setTimeout(() => {
        rendition.book.locations.generate(charPerPage).then(() => {
          try {
            localStorage.setItem(cacheKey, rendition.book.locations.save());
          } catch (e) {}
          setTotalPages(
            progressLocationsRef.current.length || rendition.book.locations.total || null
          );
          setPageFromPosition();
        });
      }, 1500);
    }
  };

  const updateAtEndState = (book, loc, fallbackCfi = "") => {
    const activeCfi = loc?.start?.cfi || fallbackCfi || "";
    if (!book || !isValidCfi(activeCfi)) {
      clearTimeout(endPanelTimerRef.current);
      setAtEnd(false);
      return;
    }

    const endCfi = loc?.end?.cfi || activeCfi;
    const endPage = getStablePageNumber(
      endCfi,
      loc?.end?.location != null
        ? loc.end.location + 1
        : loc?.start?.location != null
          ? loc.start.location + 1
          : null
    );
    const spineItem = book.spine.get(activeCfi);
    const chapterEndPage =
      spineItem?.index != null ? chapterEndPagesRef.current[spineItem.index] : null;
    const currentSectionScroll = getCurrentSectionScrollState();
    const isTouchReadyForEndPanel =
      !IS_TOUCH ||
      !currentSectionScroll?.canScroll ||
      currentSectionScroll.ratio >= READER_TOUCH_CHAPTER_END_RATIO;
    const shouldShow =
      endPage != null &&
      chapterEndPage != null &&
      endPage >= chapterEndPage &&
      isTouchReadyForEndPanel &&
      scrollNearEndRef.current;

    const nextTarget = getNextLinearSpineTarget(
      book,
      spineItem?.href || loc?.start?.href || "",
      tocRef.current
    );

    clearTimeout(endPanelTimerRef.current);
    if (shouldShow && nextTarget && !nextTarget.isChapterBoundary) {
      setAtEnd(false);
      return;
    }
    if (shouldShow) {
      endPanelTimerRef.current = setTimeout(() => setAtEnd(true), 120);
      return;
    }
    setAtEnd(false);
  };

  const resolveChapterTitle = (book, loc, fallbackCfi) => {
    const href = loc?.start?.href;
    if (href) {
      const navItem = book.navigation?.get?.(href);
      if (navItem?.label) return navItem.label.trim();
    }
    const spineItem = fallbackCfi ? book.spine.get(fallbackCfi) : null;
    if (spineItem) {
      const navItem = book.navigation?.get?.(spineItem.href);
      if (navItem?.label) return navItem.label.trim();
    }
    const logicalTocItem = getLogicalTocItemForHref(
      book,
      href || spineItem?.href || "",
      tocRef.current
    );
    if (logicalTocItem?.label) return logicalTocItem.label.trim();
    return "";
  };

  const syncDisplayedLocation = (loc) => {
    if (restoringScrollPositionRef.current) return;

    const book = renditionRef.current?.book;
    const displayCfi = loc?.start?.cfi || "";
    if (!book || !isValidCfi(displayCfi)) return;

    setLocation(displayCfi);
    locationRef.current = displayCfi;

    const page =
      getStablePageNumber(
        displayCfi,
        loc?.start?.location != null
          ? loc.start.location + 1
          : loc?.end?.location != null
            ? loc.end.location + 1
            : null
      );
    if (page != null) {
      setCurrentPage(page);
    }
    updateAtEndState(book, loc, displayCfi);

      const fallbackPercent =
      loc?.start?.percentage != null
        ? Math.round(loc.start.percentage * 1000) / 10
        : loc?.end?.percentage != null
          ? Math.round(loc.end.percentage * 1000) / 10
          : book.locations?.total > 0
            ? Math.round(book.locations.percentageFromCfi(displayCfi) * 1000) / 10
            : 0;
    const sectionHref = normalizeHrefPath(loc?.start?.href || "");
    const existingProgressFloor = getExistingProgressFloor(
      displayCfi,
      sectionHref
    );
    const percent = progressLocationsReadyRef.current
      ? getStableProgressPercent(displayCfi, fallbackPercent)
      : Math.max(fallbackPercent, existingProgressFloor);

    const chapterTitle = resolveChapterTitle(book, loc, displayCfi);
    latestProgressRef.current = {
      cfi: displayCfi,
      percent,
      chapterTitle,
      sectionHref,
      sectionScrollRatio: getCurrentSectionScrollRatio()
    };
    debugReaderProgress("sync-displayed-location", {
      cfi: displayCfi,
      percent,
      chapterTitle,
      sectionHref: latestProgressRef.current.sectionHref,
      sectionScrollRatio: latestProgressRef.current.sectionScrollRatio,
      page
    });
    queueProgressSave(latestProgressRef.current);
  };

  const syncLocationFromRendition = () => {
    try {
      const loc = renditionRef.current?.currentLocation?.();
      if (loc?.start?.cfi) {
        syncDisplayedLocation(loc);
      }
    } catch (e) {}
  };

  const navigateToHref = (href) => {
    if (!renditionRef.current) return;
    scrollNearEndRef.current = false;
    setAtEnd(false);
    if (tocNavInProgressRef.current) {
      tocNavPendingRef.current = href;
      return;
    }
    tocNavInProgressRef.current = true;
    renditionRef.current
      .display(href)
      .then(() => {
        setTimeout(() => {
          try {
            const loc = renditionRef.current?.currentLocation();
            const cfi = loc?.start?.cfi;
            if (isValidCfi(cfi)) {
              locationRef.current = cfi;
              const stablePage = getStablePageNumber(cfi, null);
              if (stablePage != null) setCurrentPage(stablePage);
            }
          } catch (e) {}
          tocNavInProgressRef.current = false;
          if (tocNavPendingRef.current) {
            const next = tocNavPendingRef.current;
            tocNavPendingRef.current = null;
            navigateToHref(next);
          }
        }, 250);
      })
      .catch(() => {
        tocNavInProgressRef.current = false;
        tocNavPendingRef.current = null;
      });
  };

  const ensureLogicalChapterViews = async (rendition, renderedSection) => {
    const manager = rendition?.manager;
    if (
      !renderedSection?.href ||
      typeof manager?.append !== "function" ||
      typeof manager?.prepend !== "function" ||
      !manager?.views
    ) {
      return;
    }

    const navToc = flattenTocItems(rendition.book?.navigation?.toc || []);
    const group = getLogicalSpineGroup(
      rendition.book,
      renderedSection.href,
      navToc
    );
    if (group.length <= 1) return;

    const renderedIndex = group.findIndex(
      (item) => item?.index === renderedSection.index
    );
    if (renderedIndex < 0) return;

    const groupKey = group.map((item) => item?.index).join(":");
    if (logicalChapterMountsInProgressRef.current.has(groupKey)) return;
    logicalChapterMountsInProgressRef.current.add(groupKey);

    const isMounted = (section) =>
      (manager.views.all?.() || []).some(
        (view) => view?.section?.index === section?.index
      );

    try {
      // Keep each EPUB document intact so saved CFIs stay valid. Only the
      // TOC-unlisted pieces of this logical chapter share its scroll container.
      for (let index = renderedIndex - 1; index >= 0; index -= 1) {
        if (!isMounted(group[index])) await manager.prepend(group[index]);
      }
      for (let index = renderedIndex + 1; index < group.length; index += 1) {
        if (!isMounted(group[index])) await manager.append(group[index]);
      }
      manager.views.show?.();
      window.requestAnimationFrame(() => rendition.reportLocation?.());
    } finally {
      logicalChapterMountsInProgressRef.current.delete(groupKey);
    }
  };

  const handleGetRendition = (rendition) => {
    renditionRef.current = rendition;
    applyReaderAppearance(rendition, readerAppearanceRef.current);
    openPendingNotificationComment();

    rendition.hooks.content.register((contents) => {
      applyReaderAppearanceToContents(contents, readerAppearanceRef.current);

      const endMarker = contents.document.createElement("div");
      endMarker.setAttribute("data-reader-end-marker", "true");
      endMarker.style.cssText =
        "height:1px;min-height:1px;opacity:0;pointer-events:none;margin:0;padding:0;";
      contents.document.body.appendChild(endMarker);

      const spacer = contents.document.createElement("div");
      spacer.style.height = "60px";
      contents.document.body.appendChild(spacer);

      contentsRef.current = contents;

      let scrollSyncTimerId = null;
      const getScrollMetrics = () => {
        const doc = contents.document;
        const docEl = doc.documentElement;
        const body = doc.body;
        const scrollingEl = doc.scrollingElement || docEl || body;
        const scrollTop = Math.max(
          contents.window.scrollY || 0,
          scrollingEl?.scrollTop || 0,
          docEl?.scrollTop || 0,
          body?.scrollTop || 0
        );
        const viewportHeight = Math.max(
          contents.window.innerHeight || 0,
          contents.window.visualViewport?.height || 0,
          docEl?.clientHeight || 0,
          scrollingEl?.clientHeight || 0
        );
        const scrollHeight = Math.max(
          scrollingEl?.scrollHeight || 0,
          docEl?.scrollHeight || 0,
          body?.scrollHeight || 0
        );
        return { scrollTop, viewportHeight, scrollHeight };
      };
        const syncFromScroll = () => {
          clearTimeout(scrollSyncTimerId);
          scrollSyncTimerId = setTimeout(() => {
            try {
              const { scrollTop, viewportHeight, scrollHeight } = getScrollMetrics();
              const distanceFromBottom = scrollHeight - (scrollTop + viewportHeight);
              const isNearBottom =
                distanceFromBottom <= READER_CHAPTER_END_SHOW_THRESHOLD;
              scrollNearEndRef.current = isNearBottom;
            } catch (e) {}
            syncLocationFromRendition();
          }, 90);
        };
      contents.window.addEventListener("scroll", syncFromScroll, {
        passive: true
      });
      contents.document.addEventListener("scroll", syncFromScroll, {
        passive: true
      });
      contents.document.addEventListener("touchend", syncFromScroll, {
        passive: true
      });
      contents.window.visualViewport?.addEventListener("resize", syncFromScroll, {
        passive: true
      });

      setTimeout(syncFromScroll, 250);

      // Poll the iframe's selection every 300ms — the only reliable approach for
      // iOS, where selectionchange inside sandboxed iframes doesn't fire and
      // touchend is consumed by the system during long-press selection.
      clearInterval(selPollRef.current);
      lastSelectionPollKeyRef.current = "";
      selPollRef.current = setInterval(() => {
        try {
          const activeSelection = (renditionRef.current?.getContents?.() || [])
            .map((candidate) => ({
              contents: candidate,
              selection: candidate?.window?.getSelection?.()
            }))
            .find(({ selection }) =>
              Boolean(selection && !selection.isCollapsed && selection.toString().trim())
            );
          const selectedContents = activeSelection?.contents || null;
          const sel = activeSelection?.selection || null;
          const text = sel ? sel.toString().trim() : "";
          const selectionKey = selectedContents
            ? `${selectedContents.sectionIndex}:${text}`
            : "";
          if (selectionKey === lastSelectionPollKeyRef.current) return;
          const hadSelection = Boolean(lastSelectionPollKeyRef.current);
          lastSelectionPollKeyRef.current = selectionKey;
          if (!sel || sel.isCollapsed || !text) {
            if (hadSelection) setSelToolbar(null);
            return;
          }
          contentsRef.current = selectedContents;
          const range = sel.getRangeAt(0);
          const cfi = selectedContents.cfiFromRange(range);
          const commentVisualAnchor = _getCommentVisualAnchorData(range);
          setSelToolbar({
            step: "main",
            x: 0,
            y: 0,
            cfi,
            text,
            commentAnchorCfi: _getCommentAnchorTarget(selectedContents, range, cfi),
            commentDisplayParagraphText:
              _getCommentDisplayFingerprintFromRange(range),
            commentVisualOffsetTop:
              commentVisualAnchor?.offsetTop ?? null
          });
          setManageSheet(null);
          setNoteSheet(null);
        } catch (_) {}
      }, 300);
    });

    rendition.on("rendered", (section) => {
      ensureLogicalChapterViews(rendition, section).catch(() => {});
      setTimeout(() => {
        highlightsRef.current.forEach((h) =>
          _markAnnotationInteractive(rendition, h.cfi)
        );
        Object.keys(commentAnchorsRef.current).forEach((cfi) => {
          _upsertCommentMarker(
            rendition,
            cfi,
            Math.max(1, (commentsDataRef.current[cfi] || []).length)
          );
        });
        Object.entries(commentsDataRef.current).forEach(([cfi, comments]) => {
          if (comments?.length) _upsertCommentMarker(rendition, cfi, comments.length);
        });
        const noteHighlights = highlightsRef.current.filter((h) => h.note_text);
        noteHighlights.forEach((h) => _insertNoteDot(rendition, h.cfi, h.color));
      }, 600);
    });

    if (!IS_TOUCH) {
      rendition.on("selected", (cfiRange, contents) => {
        contentsRef.current = contents;
        const selection = contents.window.getSelection();
        if (!selection || selection.isCollapsed || !selection.toString().trim())
          return;
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const iframeEl =
          contents.window?.frameElement ||
          document.querySelector(".reader-content iframe");
        if (!iframeEl) return;
        const iframeRect = iframeEl.getBoundingClientRect();
        const commentVisualAnchor = _getCommentVisualAnchorData(range);
        setSelToolbar({
          step: "main",
          x: iframeRect.left + rect.left + rect.width / 2,
          y: iframeRect.top + rect.top,
          cfi: cfiRange,
          text: selection.toString().trim(),
          commentAnchorCfi: _getCommentAnchorTarget(contents, range, cfiRange),
          commentDisplayParagraphText:
            _getCommentDisplayFingerprintFromRange(range),
          commentVisualOffsetTop:
            commentVisualAnchor?.offsetTop ?? null
        });
        setManageSheet(null);
        setNoteSheet(null);
      });
    }

    rendition.on("relocated", (loc) => {
      setSelToolbar(null);
      const pendingRestore = pendingScrollRestoreRef.current;
      const currentHref = normalizeHrefPath(loc?.start?.href || "");
      debugReaderProgress("relocated", {
        startCfi: loc?.start?.cfi || "",
        startHref: currentHref,
        startLocation: loc?.start?.location ?? null,
        startPercentage: loc?.start?.percentage ?? null,
        pendingRestore
      });
      if (
        pendingRestore?.href &&
        hrefsMatch(pendingRestore.href, currentHref) &&
        pendingRestore.ratio != null &&
        pendingRestore.ratio >= 0
      ) {
        pendingScrollRestoreRef.current = null;
        restoringScrollPositionRef.current = true;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            restoreSectionScrollPosition(pendingRestore);
          });
        });
        return;
      }
      syncDisplayedLocation(loc);
      if (tempSearchHighlightRef.current) {
        const currentHref = (loc?.start?.href || "").split("#")[0];
        if (
          tempSearchHighlightHrefRef.current &&
          currentHref &&
          currentHref !== tempSearchHighlightHrefRef.current
        ) {
          clearTempSearchHighlight();
        }
      }
      const navToc = flattenTocItems(rendition.book.navigation?.toc || []);
      if (navToc.length && loc.start?.href) {
        const startHref = normalizeHrefPath(loc.start.href);
        setCurrentSectionHref(startHref);
        const idx = navToc.findIndex((item) => {
          return hrefsMatch(item.href || "", startHref);
        });
        setCurrentTocIndex(idx);
      } else if (loc.start?.href) {
        setCurrentSectionHref(normalizeHrefPath(loc.start.href));
      }
    });

    rendition.book.ready.then(async () => {
      await prepareProgressLocations(rendition.book);
      applyReaderAppearance(rendition, readerAppearanceRef.current);
      applyLocations(rendition, readerAppearanceRef.current);
      setToc(flattenTocItems(rendition.book.navigation.toc || []));
      highlightsRef.current.forEach((h) =>
        _applyToRendition(rendition, h.cfi, h.color, !!h.note_text)
      );
      Object.keys(commentAnchorsRef.current).forEach((cfi) => {
        _upsertCommentMarker(
          rendition,
          cfi,
          Math.max(1, (commentsDataRef.current[cfi] || []).length)
        );
      });
      // Load and apply comment counts
      _loadAndApplyCommentCounts(rendition);
    });
  };

  const handleLocationChanged = (cfi) => {
    if (skipNext.current) {
      skipNext.current = false;
      return;
    }
    setLocation(cfi);
    locationRef.current = cfi;
  };

  const isReady = epub && epubData && totalPages !== null;
  if (!epub && !epubData) return null;
  const nextChapter = getNextChapterFromBook();

  // Shared content for selection toolbar (used in both floating and bottom-bar variants)
  const selToolbarContent = selToolbar && (
    <>
      <button className="sel-btn" onClick={handleHighlightFromSelection}>
        Highlight
      </button>
      <div className="sel-divider" />
      <button className="sel-btn" onClick={handleCommentFromSelection}>
        Comment
      </button>
      <div className="sel-divider" />
      <button
        className="sel-btn"
        onClick={() => {
          openReaderChatWithQuote(selToolbar.text);
          setSelToolbar(null);
        }}
      >
        Send as Quote
      </button>
    </>
  );

  return (
    <div
      className={`reader-page reader-page--${readerAppearance.theme}${readerAppearance.theme === "dark" ? " reader-theme--dark" : ""}`}
    >
      {!isReady && (
        <div className="reader-loading-overlay">
          <span className="material-symbols-rounded reader-loading-icon">
            progress_activity
          </span>
          <p className="reader-loading-text mb-0">{readerLoadingMessage}</p>
        </div>
      )}

      <div className="container-fluid d-flex justify-content-between align-items-center p-4 reader-topbar">
        <span
          className="material-symbols-rounded reader-topbar-btn"
          onClick={() => {
            if (showToc) {
              closeToc();
            } else {
              setIsTocClosing(false);
              clearTimeout(tocCloseTimerRef.current);
              setShowToc(true);
            }
          }}
        >
          menu_open
        </span>
        <span
          className="material-symbols-rounded reader-topbar-btn"
          onClick={() => {
            flushReadingProgress().finally(() => {
              window.dispatchEvent(new Event("reader-closed"));
              navigate(-1);
            });
          }}
        >
          close
        </span>
      </div>

      <div className="reader-content">
        {epubData && (
          <EpubView
            key={`reader-book-${currentEpubId}-${readerBookVersion}`}
            url={epubData}
            location={location}
            locationChanged={handleLocationChanged}
            getRendition={handleGetRendition}
            epubOptions={{ flow: "scrolled-doc" }}
          />
        )}
      </div>

      {showToc && (
        <div
          className={`reader-toc-overlay${isTocClosing ? " reader-toc-overlay--closing" : ""}`}
          onClick={closeToc}
        >
          <div
            className={`reader-toc-sheet${isTocClosing ? " reader-toc-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="reader-toc-header">
              <span className="reader-toc-heading">Table of Contents</span>
            </div>
            <div className="reader-toc-body">
              {toc.map((item, i) => {
                const appendedChapter = getAppendedChapterForTocItem(
                  toc,
                  i,
                  appendedChapters
                );
                const showChapterMenu = currentUserIsAdmin && appendedChapter;

                return (
                  <div key={i} className="reader-toc-item-row">
                    <button
                      className="reader-toc-item"
                      onClick={() => {
                        closeToc();
                        navigateToHref(item.href);
                      }}
                    >
                      <span>{item.label.trim()}</span>
                      {appendedChapter ? (
                        <span className="reader-toc-item__appended-label">(Added)</span>
                      ) : null}
                    </button>
                    {showChapterMenu ? (
                      <div className="reader-toc-item-menu">
                        <button
                          type="button"
                          className="reader-toc-item-menu__trigger"
                          onClick={() =>
                            setChapterMenuOpenId((prev) =>
                              prev === appendedChapter.id ? null : appendedChapter.id
                            )
                          }
                          aria-label="Chapter actions"
                        >
                          <span className="material-symbols-rounded">more_horiz</span>
                        </button>
                        {chapterMenuOpenId === appendedChapter.id ? (
                          <div className="reader-toc-item-menu__popover">
                            <button
                              type="button"
                              className="reader-toc-item-menu__danger"
                              onClick={() => handleDeleteAppendedChapter(appendedChapter.id)}
                            >
                              Delete chapter
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <button
                className="reader-toc-item reader-toc-item--add-chapter"
                onClick={openAddChapterSheet}
                type="button"
              >
                <span className="material-symbols-rounded reader-toc-item__icon">
                  add
                </span>
                Add chapter
              </button>
            </div>
            <div className="reader-toc-actions">
              <button
                className="btn-neutral reader-toc-action"
                onClick={() => openReaderTool("search")}
              >
                <span className="material-symbols-rounded reader-toc-action-icon" style={{ fontSize: 18 }}>
                  search
                </span>
                Search Book
              </button>
              <button
                className="btn-neutral reader-toc-action"
                onClick={() => openReaderTool("appearance")}
              >
                <span className="material-symbols-rounded reader-toc-action-icon" style={{ fontSize: 18 }}>
                  palette
                </span>
                Reader Appearance
              </button>
              <button
                className="btn-neutral reader-toc-action"
                onClick={() => openReaderTool("highlights")}
              >
                <span className="material-symbols-rounded reader-toc-action-icon" style={{ fontSize: 18 }}>
                  format_ink_highlighter
                </span>
                {`Annotations • ${readerAnnotationCount}`}
              </button>
              <button
                className={`${isChatPinned ? "btn-cta" : "btn-neutral"} reader-toc-action`}
                onClick={() => setIsChatPinned((prev) => !prev)}
              >
                <span className="material-symbols-rounded reader-toc-action-icon" style={{ fontSize: 18 }}>
                  chat_add_on
                </span>
                Pin Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddChapterOpen && (
        <div className="reader-add-chapter-sheet" onClick={(event) => event.stopPropagation()}>
          <div className="reader-add-chapter-sheet__header">
            <span className="reader-add-chapter-sheet__title">Add Chapter</span>
            <button
              type="button"
              className="reader-add-chapter-sheet__close"
              onClick={closeAddChapterSheet}
              aria-label="Close add chapter"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <input
            type="text"
            className="btn-neutral reader-add-chapter-sheet__title-input"
            value={newChapterTitle}
            onChange={(event) => setNewChapterTitle(event.target.value)}
            placeholder="Chapter title"
          />
          <div
            ref={addChapterEditorRef}
            className="btn-neutral reader-add-chapter-sheet__input"
            contentEditable
            suppressContentEditableWarning
            onInput={handleAddChapterInput}
            onPaste={handleAddChapterPaste}
            data-placeholder="Paste the new chapter here..."
            role="textbox"
            aria-multiline="true"
            spellCheck={false}
          />
          {addChapterError ? (
            <p className="reader-add-chapter-sheet__error mb-0">{addChapterError}</p>
          ) : null}
          <button
            type="button"
            className="btn-cta reader-add-chapter-sheet__submit"
            onClick={handleAddChapterSubmit}
            disabled={isAddingChapter}
          >
            {isAddingChapter ? "Adding..." : "Add New Chapter"}
          </button>
        </div>
      )}

      {isChatPinned && (
        <button
          className="reader-chat-pin"
          type="button"
          aria-label="Pinned chat"
          onClick={openReaderChat}
        >
          <span className="material-symbols-rounded reader-chat-pin__icon">
            forum
          </span>
          {readerChatUnreadCount > 0 && (
            <span className="reader-chat-pin__badge">
              {readerChatUnreadCount}
            </span>
          )}
        </button>
      )}

      <ReaderChatModal
        isOpen={isReaderChatOpen}
        onClose={() => setIsReaderChatOpen(false)}
        initialQuote={pendingReaderChatQuote}
        onInitialQuoteApplied={() => setPendingReaderChatQuote(null)}
      />

      {activeReaderTool && (
        <div
          className={`reader-tool-overlay${isReaderToolClosing ? " reader-tool-overlay--closing" : ""}`}
          onClick={closeReaderTool}
        >
          <div
            className={`reader-tool-sheet${activeReaderTool === "search" ? " reader-tool-sheet--search" : ""}${activeReaderTool === "highlights" ? " reader-tool-sheet--library" : ""}${activeReaderTool === "appearance" ? " reader-tool-sheet--appearance" : ""}${activeReaderTool === "appearance" && isFontMenuOpen ? " reader-tool-sheet--appearance-expanded" : ""}${isReaderToolClosing ? " reader-tool-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            {activeReaderTool !== "search" &&
              activeReaderTool !== "highlights" &&
              activeReaderTool !== "appearance" && (
              <div className="highlight-note-header">
                <span className="highlight-note-title">
                  {readerToolTitles[activeReaderTool]}
                </span>
              </div>
            )}
            <div className="reader-tool-body">
              {activeReaderTool === "search" && (
                <div className="reader-search-panel">
                  <div className="reader-search-bar btn-neutral">
                    <span
                      className="material-symbols-rounded"
                      style={{ fontSize: 20, opacity: 0.7 }}
                    >
                      search
                    </span>
                    <input
                      className="reader-search-input"
                      type="text"
                      placeholder="Search Book"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="reader-search-results">
                    {isSearchLoading && (
                      <div className="reader-search-state">Searching book...</div>
                    )}
                    {!isSearchLoading && !searchQuery.trim() && (
                      <div className="reader-search-state">
                        Search by word or phrase to find matches in this book.
                      </div>
                    )}
                    {!isSearchLoading &&
                      searchQuery.trim() &&
                      searchResults.length === 0 && (
                        <div className="reader-search-state">
                          No matches found for "{searchQuery.trim()}".
                        </div>
                      )}
                    {!isSearchLoading &&
                      searchResults.map((result) => (
                        <button
                          key={result.id}
                          className="reader-search-result"
                          onClick={() => openSearchResult(result)}
                        >
                          <div className="reader-search-result-meta">
                            <span className="reader-search-result-chapter">
                              {result.chapterLabel}
                            </span>
                            {result.pageNumber != null && (
                              <span className="reader-search-result-page">
                                Page {result.pageNumber}
                              </span>
                            )}
                          </div>
                          <div className="reader-search-result-excerpt">
                            {renderSearchExcerpt(result.excerpt, searchQuery.trim())}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
              {activeReaderTool === "highlights" && (
                <div className="reader-library-panel">
                  <div className="reader-library-book">
                    <div className="reader-library-book-row d-flex align-items-center gap-4">
                      <div className="uploads-left d-flex align-items-center justify-content-center reader-library-cover">
                        <span className="uploads-left-letter">
                          {epub?.title?.charAt(0)?.toUpperCase() || "B"}
                        </span>
                      </div>
                      <div className="uploads-middle reader-library-book-meta">
                        <h5 className="mb-0">{epub?.title || "Untitled Book"}</h5>
                        <p className="uploads-author mb-0">{epub?.author || "Unknown author"}</p>
                      </div>
                      <button
                        className="btn-neutral reader-library-export-btn"
                        onClick={handleExportComments}
                        disabled={!readerCommentEntries.length}
                        aria-label="Export comments"
                        title="Export comments"
                      >
                        <span className="material-symbols-rounded">
                          share_windows
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="reader-library-switcher btn-neutral">
                    <button
                      className={`reader-library-switch${readerLibraryTab === "highlights" ? " reader-library-switch--active" : ""}`}
                      onClick={() => setReaderLibraryTab("highlights")}
                    >
                      {`Highlights • ${readerHighlightEntries.length}`}
                    </button>
                    <button
                      className={`reader-library-switch${readerLibraryTab === "comments" ? " reader-library-switch--active" : ""}`}
                      onClick={() => setReaderLibraryTab("comments")}
                    >
                      {`Comments • ${readerCommentEntries.length}`}
                    </button>
                  </div>

                  <div className="reader-library-results">
                    {readerLibraryTab === "highlights" && readerHighlightEntries.length === 0 && (
                      <div className="reader-library-empty">
                        Your highlights for this book will appear here.
                      </div>
                    )}
                    {readerLibraryTab === "highlights" &&
                      readerHighlightEntries.map((item) => (
                        <div
                          key={item.id}
                          className={`reader-library-swipe-item${swipedHighlightId === item.id ? " reader-library-swipe-item--open" : ""}`}
                          onTouchStart={(e) => handleHighlightSwipeStart(item.id, e)}
                          onTouchMove={(e) => handleHighlightSwipeMove(item.id, e)}
                          onTouchEnd={() => handleHighlightSwipeEnd(item.id)}
                          onTouchCancel={() => handleHighlightSwipeEnd(item.id)}
                        >
                          <button
                            type="button"
                            className="reader-library-delete-action"
                            onClick={() => handleHighlightDelete(item)}
                            aria-label="Delete highlight"
                          >
                            <span className="material-symbols-rounded">
                              delete
                            </span>
                          </button>
                          <button
                            type="button"
                            className={`reader-library-item reader-library-item--swipeable${item.text ? " reader-library-item--with-note" : ""}`}
                            onClick={() => handleHighlightItemOpen(item)}
                          >
                            <div className="reader-library-item-meta">
                              <span className="reader-library-item-date">
                                {formatReaderToolDate(item.createdAt)}
                              </span>
                              {item.pageNumber != null && (
                                <span className="reader-library-item-page">
                                  Page {item.pageNumber}
                                </span>
                              )}
                            </div>
                            {item.excerpt && (
                              <div className="reader-library-item-text reader-library-item-text--comment-excerpt">
                                <span
                                  className="reader-library-comment-excerpt"
                                  style={{ backgroundColor: withAlpha(item.color, 0.5) }}
                                >
                                  {item.excerpt}
                                </span>
                              </div>
                            )}
                            {item.text && (
                              <div className="reader-library-comment-body">
                                {item.text}
                              </div>
                            )}
                          </button>
                        </div>
                      ))}

                    {readerLibraryTab === "comments" && readerCommentEntries.length === 0 && (
                      <div className="reader-library-empty">
                        Your comments for this book will appear here.
                      </div>
                    )}
                    {readerLibraryTab === "comments" &&
                      readerCommentEntries.map((item) => (
                        <button
                          key={item.id}
                          className="reader-library-item"
                          onClick={() => openCommentResult(item)}
                        >
                          <div className="reader-library-item-meta">
                            <span className="reader-library-item-date">
                              {formatReaderToolDate(item.createdAt)}
                            </span>
                            {item.pageNumber != null && (
                              <span className="reader-library-item-page">
                                Page {item.pageNumber}
                              </span>
                            )}
                          </div>
                          {item.excerpt && (
                            <div className="reader-library-item-text reader-library-item-text--comment-excerpt">
                              <span className="reader-library-comment-excerpt reader-library-comment-excerpt--comment-tab">
                                "{item.excerpt}"
                              </span>
                            </div>
                          )}
                          <div className="reader-library-comment-body">
                            {item.text}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
              {activeReaderTool === "appearance" && (
                <div className="reader-appearance-panel">
                  <div className="reader-appearance-header">
                    <div className="reader-appearance-header-icon-wrap">
                      <span className="material-symbols-rounded reader-appearance-header-icon">
                        auto_stories
                      </span>
                    </div>
                    <span className="highlight-note-title mb-0">
                      Reader Appearance
                    </span>
                  </div>

                  <div className="reader-appearance-section">
                    <div className="reader-appearance-theme-grid">
                      {READER_THEME_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`reader-appearance-theme-option${readerAppearance.theme === option.id ? " reader-appearance-theme-option--active" : ""}`}
                          onClick={() => handleAppearanceThemeSelect(option.id)}
                        >
                          <p className="mb-0">{option.label}</p>
                          <div className={`reader-appearance-theme-card ${option.previewClass}`}>
                            <span className="reader-appearance-theme-preview-text">
                              Aa
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="reader-appearance-section">
                    <div className="reader-appearance-stepper">
                      <button
                        type="button"
                        className="reader-appearance-stepper-btn"
                        onClick={() => handleAppearanceTextSizeChange(-1)}
                        aria-label="Decrease text size"
                      >
                        -
                      </button>
                      <div className="reader-appearance-stepper-value">
                        <p className="mb-0 fw-medium">
                          {`Text Size • ${readerAppearance.textSize}px`}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="reader-appearance-stepper-btn"
                        onClick={() => handleAppearanceTextSizeChange(1)}
                        aria-label="Increase text size"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className="reader-appearance-section">
                    <button
                      type="button"
                      className="reader-appearance-row"
                      onClick={() => setIsFontMenuOpen((open) => !open)}
                    >
                      <p className="mb-0 fw-medium">Font</p>
                      <div className="reader-appearance-row-value">
                        <p className="mb-0">{readerAppearance.fontFamily}</p>
                        <span className="material-symbols-rounded">
                          {isFontMenuOpen ? "expand_less" : "chevron_right"}
                        </span>
                      </div>
                    </button>
                    {isFontMenuOpen && (
                      <div className="reader-font-picker">
                        {READER_FONT_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className={`reader-font-option ${option.previewClass}${readerAppearance.fontFamily === option.id ? " reader-font-option--active" : ""}`}
                            onClick={() =>
                              handleAppearanceFontFamilyChange(option.id)
                            }
                          >
                            <span className="reader-font-option-name">
                              {option.label}
                            </span>
                            <span className="reader-font-option-preview">
                              The reader text preview
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="reader-appearance-section reader-appearance-section--line-spacing">
                    <div className="reader-appearance-row reader-appearance-row--static">
                      <p className="mb-0 fw-medium">Line Spacing</p>
                      <p className="mb-0">
                        {readerAppearance.lineSpacing.toFixed(2)}
                      </p>
                    </div>
                    <div className="reader-appearance-slider-wrap">
                      <input
                        type="range"
                        min="1"
                        max="2"
                        step="0.05"
                        value={readerAppearance.lineSpacing}
                        className="reader-appearance-slider"
                        style={{
                          "--reader-slider-progress": `${((readerAppearance.lineSpacing - 1) / 1) * 100}%`
                        }}
                        onChange={(e) =>
                          handleAppearanceLineSpacingChange(e.target.value)
                        }
                        aria-label="Line spacing"
                      />
                    </div>
                    <div className="reader-appearance-divider" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="reader-bottombar">
        <span className="reader-page-count">
          {totalPages === null
            ? "Counting pages…"
            : currentPage
              ? `${currentPage} / ${totalPages}`
              : `${totalPages} pages`}
        </span>
      </div>

      {atEnd && nextChapter && (
        <div className="reader-end-panel">
          <div className="reader-end-row">
            <div className="reader-end-meta">
              <div className="reader-end-up-next">Up next</div>
              <div className="reader-end-next-title">
                {nextChapter.label.trim()}
              </div>
            </div>
            <button
              className="reader-end-btn--cta"
              onClick={() => navigateToHref(nextChapter.href)}
            >
              <span
                className="material-symbols-rounded"
                style={{ fontSize: 16 }}
              >
                arrow_forward
              </span>
            </button>
          </div>
        </div>
      )}

      {selToolbar && IS_TOUCH && (
        <div
          className="sel-bottom-bar"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {selToolbarContent}
          <div style={{ flex: 1 }} />
          <button
            className="sel-btn sel-btn--close"
            onClick={() => setSelToolbar(null)}
          >
            ✕
          </button>
        </div>
      )}
      {selToolbar && !IS_TOUCH && (
        <div
          className="sel-floating"
          style={{
            top: selToolbar.y - 8,
            left: selToolbar.x,
            transform: "translate(-50%, -100%)"
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {selToolbarContent}
          <div className="sel-floating-arrow" />
        </div>
      )}

      {manageSheet && (
        <div
          className={`highlight-sheet-overlay${isManageSheetClosing ? " highlight-sheet-overlay--closing" : ""}`}
          onClick={closeManageSheet}
        >
          <div
            className={`highlight-manage-sheet${isManageSheetClosing ? " highlight-manage-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="highlight-manage-colors">
              {getHighlightPalette().map((color) => (
                <button
                  key={color}
                  className={`sel-swatch${manageSheet.color === color ? " sel-swatch--active" : ""}`}
                  style={{ background: color }}
                  onClick={() => changeHighlightColor(manageSheet.cfi, color)}
                />
              ))}
            </div>
            <div className="highlight-manage-actions">
              <button
                className="highlight-manage-btn"
                onClick={() => {
                  setNoteSheet({
                    cfi: manageSheet.cfi,
                    color: manageSheet.color,
                    text: manageSheet.noteText,
                    isEditing: manageSheet.hasNote
                  });
                  setManageSheet(null);
                }}
              >
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 17 }}
                >
                  edit_note
                </span>
                {manageSheet.hasNote ? "Edit Note" : "Add Note"}
              </button>
              <button
                className="highlight-manage-btn"
                onClick={() => {
                  setAddCommentSheet({
                    cfi: manageSheet.cfi,
                    selectedText: manageSheet.highlightedText || "",
                    text: ""
                  });
                  setManageSheet(null);
                }}
              >
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 17 }}
                >
                  comment
                </span>
                Add Comment
              </button>
              {manageSheet.highlightedText && (
                <button
                  className="highlight-manage-btn"
                  onClick={() => {
                    openReaderChatWithQuote(manageSheet.highlightedText);
                    setManageSheet(null);
                  }}
                >
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: 17 }}
                  >
                    send
                  </span>
                  Send as Quote
                </button>
              )}
              <button
                className="highlight-manage-btn highlight-manage-btn--danger"
                onClick={() =>
                  deleteHighlight(manageSheet.cfi, manageSheet.color)
                }
              >
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 17 }}
                >
                  delete
                </span>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {noteSheet && (
        <div
          className={`highlight-sheet-overlay${isNoteSheetClosing ? " highlight-sheet-overlay--closing" : ""}`}
          onClick={closeNoteSheet}
        >
          <div
            className={`highlight-note-sheet highlight-note-sheet--note${isNoteSheetClosing ? " highlight-note-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="highlight-note-header">
              <div
                className="highlight-note-color-dot"
                style={{ background: normalizeHighlightColor(noteSheet.color) }}
              />
              <span className="highlight-note-title">
                {noteSheet.isEditing ? "Edit Note" : "Add Note"}
              </span>
              <button
                className="highlight-note-save highlight-note-save--header"
                onClick={() => handleSaveNote(noteSheet.text)}
              >
                Save
              </button>
            </div>
            <div className="highlight-note-body">
              <textarea
                className="highlight-note-textarea"
                placeholder="Write a note…"
                value={noteSheet.text}
                onChange={(e) =>
                  setNoteSheet((n) => ({ ...n, text: e.target.value }))
                }
              />
            </div>
            {noteSheet.isEditing && (
              <div className="highlight-note-footer">
                <button
                  className="highlight-note-delete"
                  onClick={() => handleSaveNote("")}
                >
                  Delete Note
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {commentsSheet && (
        <div
          className={`highlight-sheet-overlay${isCommentsSheetClosing ? " highlight-sheet-overlay--closing" : ""}`}
          onClick={closeCommentsSheet}
        >
          <div
            className={`highlight-note-sheet highlight-note-sheet--comments${isCommentsSheetClosing ? " highlight-note-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="highlight-note-header">
              <span className="highlight-note-title">
                {`Annotations (${activeCommentCount})`}
              </span>
              <span
                className="material-symbols-rounded highlight-note-close"
                onClick={closeCommentsSheet}
              >
                close
              </span>
            </div>
            <div className="highlight-note-body" style={{ padding: 0 }}>
              {commentsSheet.offlineOnly ? (
                <div className="comment-thread-empty comment-thread-empty--offline">
                  Comments are available only while you're online. We'll load them
                  automatically when your connection comes back.
                </div>
              ) : (
                <CommentThread
                  comments={activeComments}
                  highlightedText={commentsSheet.highlightedText || ""}
                  focusCommentId={commentsSheet.focusCommentId || null}
                  chapterTitle={
                    resolveChapterTitle(
                      renditionRef.current?.book,
                      renditionRef.current?.currentLocation?.(),
                      commentsSheet.cfi
                    ) || latestProgressRef.current?.chapterTitle || ""
                  }
                  epubId={id}
                  cfi={commentsSheet.cfi}
                  onCommentDeleted={_handleCommentDeleted}
                  onReplyAdded={_handleReplyAdded}
                  onCommentLiked={_handleCommentLiked}
                  onCommentsReload={() => _loadCommentsApi(commentsSheet.cfi)}
                />
              )}
            </div>
            {!commentsSheet.offlineOnly && (
              <div className="highlight-note-footer">
                <button
                  className="highlight-note-save highlight-note-save--outline"
                  onClick={() =>
                    setAddCommentSheet({
                      cfi: commentsSheet.cfi,
                      anchorCfi: commentsSheet.cfi,
                      selectedText: commentsSheet.highlightedText || "",
                      text: ""
                    })
                  }
                >
                  Add Comment
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {addCommentSheet && (
        <div
          className={`highlight-sheet-overlay${isAddCommentSheetClosing ? " highlight-sheet-overlay--closing" : ""}`}
          onClick={closeAddCommentSheet}
        >
          <div
            className={`highlight-note-sheet${isAddCommentSheetClosing ? " highlight-note-sheet--closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="highlight-note-header">
              <span className="highlight-note-title">Add Comment</span>
              <button
                className="highlight-note-save highlight-note-save--header highlight-note-save--send"
                onClick={() => handleSendComment(addCommentSheet.text)}
                disabled={!addCommentSheet.text.trim()}
              >
                Send
              </button>
            </div>
            {addCommentSheet.selectedText && (
              <div className="add-comment-quote">
                {addCommentSheet.selectedText.length > 140
                  ? addCommentSheet.selectedText.slice(0, 140) + "…"
                  : addCommentSheet.selectedText}
              </div>
            )}
            <div className="highlight-note-body">
              <textarea
                className="highlight-note-textarea"
                placeholder="Write your comment…"
                value={addCommentSheet.text}
                onChange={(e) =>
                  setAddCommentSheet((s) => ({ ...s, text: e.target.value }))
                }
                autoFocus
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReaderPage;
